import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CampaignReviewTabs } from './CampaignReviewTabs';
import { creativeReviewLevel, type MetaReviewLevel } from '../metaReviewLevels';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Check, Brain, Target, Megaphone, Loader2, Image, Upload, Sparkles, RefreshCw, Eye, Edit3, Save, MessageCircle, Heart, Share2, Bookmark, MoreHorizontal, ChevronLeft, ChevronRight, Trash2, Instagram, Clock, Calendar, Layers, Zap, Activity, Award, TrendingUp, BarChart2, Maximize2, Minimize2, Video, Film, Play, Download, Move, Pause, DollarSign, ArrowRight, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import { campaignsApi, Campaign, CampaignBriefing, CampaignAudience, CampaignCreative, MCAGeneratePayload, VideoProvider, ProductCatalogItem, KeywordResearchResult } from '../campaignsApi';
import { KeywordResearchPanel } from './KeywordResearchPanel';
import { compositeLogoOnScene } from '../logoCompositor';
import { LogoPositionerModal } from './LogoPositionerModal';
import { VideoGenerationModal } from './VideoGenerationModal';
import { VideoStudioPanel } from '../video-studio/VideoStudioPanel';
import { CampaignVideoStudioTab } from '../video-studio/CampaignVideoStudioTab';
import { synthesizeCreativeVideoBlob } from '../videoSynthesizer';
import { downloadVideoFile } from '../video-studio/videoDownload';
import { useSessionStore } from '../../../stores/sessionStore';
import { useTheme } from '../../../components/theme/ThemeProvider';
import { getAuthHeaders } from '../../../services/http';
import { toast } from 'sonner';
import './campaign-story-preview.css';
import { campaignChannels, channelDestinationError, type AdChannel } from '../campaignChannels';
import { CampaignChannelSettings } from './CampaignChannelSettings';
import { CampaignTrafficDestination } from './CampaignTrafficDestination';
import { CampaignChannelSummary } from './CampaignChannelSummary';
import { CampaignChannelActions } from './CampaignChannelActions';
import { CampaignJourneyGuide } from './CampaignJourneyGuide';
import { useGenerateGoogleProposals } from '../useGenerateGoogleProposals';
import { GoogleSearchReview } from './GoogleSearchReview';
import { GoogleCampaignResults } from './GoogleCampaignResults';
import { campaignTextList } from '../campaignJourney';
import { CampaignPublicationJourney } from './CampaignPublicationJourney';
import { DeleteCampaignDraftButton } from './DeleteCampaignDraftButton';

interface CampaignDrawerProps {
  campaign: Campaign;
  onClose: () => void;
}

export function CampaignDrawer({ campaign, onClose }: CampaignDrawerProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const channels = campaignChannels(campaign);

  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'briefing' | 'strategy' | 'video-studio' | 'instagram' | 'intelligence'>('briefing');
  const [selectedStudioCreativeId, setSelectedStudioCreativeId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true); // Padrão tela ampla / maximizada
  const [editingCreative, setEditingCreative] = useState<CampaignCreative | null>(null);
  const [previewCreativeId, setPreviewCreativeId] = useState<string | null>(null);
  const [reviewLevel, setReviewLevel] = useState<MetaReviewLevel>('unaware');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isSynthesizingAll, setIsSynthesizingAll] = useState(false);
  const [synthProgress, setSynthProgress] = useState<string>('');
  const [addCommentTrigger, setAddCommentTrigger] = useState(true);

  // Modal de Posicionamento Manual Interativo da Logo em Canvas
  const [logoPositioningCreative, setLogoPositioningCreative] = useState<CampaignCreative | null>(null);

  // Modal de Imagem Ampliada & Galeria de Histórico
  const [zoomedCreative, setZoomedCreative] = useState<CampaignCreative | null>(null);
  const [selectedZoomUrl, setSelectedZoomUrl] = useState<string | null>(null);
  const [zoomMediaTab, setZoomMediaTab] = useState<'image' | 'video'>('image');
  const [downloadingVideoFormat, setDownloadingVideoFormat] = useState<'mp4' | 'webm' | null>(null);
  const [downloadingVideoProgress, setDownloadingVideoProgress] = useState<string>('');

  const handleDownloadVideo = async (videoUrl: string, creativeId: string, format: 'mp4' | 'webm') => {
    try {
      setDownloadingVideoFormat(format);
      await downloadVideoFile(videoUrl, {
        filename: `video_${creativeId}.${format}`,
        format,
        onProgress: (p) => setDownloadingVideoProgress(p),
      });
      toast.success(`Vídeo baixado com sucesso em ${format.toUpperCase()}!`);
    } catch (err: any) {
      console.error('Falha ao baixar vídeo:', err);
      toast.error('Não foi possível concluir o download do vídeo.');
    } finally {
      setDownloadingVideoFormat(null);
      setDownloadingVideoProgress('');
    }
  };

  // Form states for Upgrade
  const [niche, setNiche] = useState('');
  const [painPoints, setPainPoints] = useState('');
  const [tone, setTone] = useState('persuasivo_autoridade');
  const [keepImagesOnUpgrade, setKeepImagesOnUpgrade] = useState(true);

  // Configuração de Chave de Vídeo IA (Fal.ai / Replicate / OmniRouter)
  const [isVideoKeyModalOpen, setIsVideoKeyModalOpen] = useState(false);
  const [videoApiKeyInput, setVideoApiKeyInput] = useState('');
  const [videoApiProvider, setVideoApiProvider] = useState<VideoProvider>('fal');
  const [isVideoGenerationModalOpen, setIsVideoGenerationModalOpen] = useState(false);
  const [videoGenerationProvider, setVideoGenerationProvider] = useState<VideoProvider>('fal');
  const [pendingVideoCreativeId, setPendingVideoCreativeId] = useState<string | null>(null);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [isVideoStudioOpen, setIsVideoStudioOpen] = useState(false);
  const [videoStudioCreative, setVideoStudioCreative] = useState<{ id: string; title: string } | null>(null);

  // Modal de Publicação de Stories com Seleção de Steps e Imagens
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [storySelectedStep, setStorySelectedStep] = useState<number>(5);
  const [storySelectedImageUrl, setStorySelectedImageUrl] = useState<string | null>(null);
  const [storyCustomCaption, setStoryCustomCaption] = useState<string>('');

  // Modal de Prévia e Edição da Legenda do Carrossel
  const [isCarouselModalOpen, setIsCarouselModalOpen] = useState(false);
  const [carouselCustomCaption, setCarouselCustomCaption] = useState<string>('');
  
  // Duração de Vídeo (Padrão 10s - Configurável: 10s, 15s, 30s)
  const [videoDuration, setVideoDuration] = useState<number>(10);

  // Edição Direta do Nome da Campanha
  const [isEditingName, setIsEditingName] = useState(false);
  const [campaignNameInput, setCampaignNameInput] = useState(campaign.name || '');

  // Estado do Ciclo de Vida da Campanha e Orçamento Diário
  const [currentStatus, setCurrentStatus] = useState(campaign.status || 'draft');
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetDailyInput, setBudgetDailyInput] = useState<number>(() => {
    return (campaign.budget as any)?.daily || 50;
  });

  useEffect(() => {
    setCurrentStatus(campaign.status || 'draft');
  }, [campaign.status]);

  const session = useSessionStore((state) => state.session);
  const activeTenantId = useSessionStore((state) => state.activeTenantId);
  const token = session?.access_token || '';
  const init = token ? { headers: getAuthHeaders(token) } : undefined;
  const googleProposals = useGenerateGoogleProposals(campaign, init);

  const updateCampaignNameMutation = useMutation({
    mutationFn: (name: string) => campaignsApi.updateCampaign(campaign.id, { name }, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      setIsEditingName(false);
    },
    onError: (err: any) => {
      alert('Erro ao atualizar nome da campanha: ' + (err.message || 'Falha na requisição'));
    }
  });

  const videoKeyQuery = useQuery({
    queryKey: ['campaigns', 'video-key-status'],
    queryFn: () => campaignsApi.getVideoKeyStatus(init),
    enabled: !!token
  });
  const selectedVideoKeyStatus = videoKeyQuery.data?.providers?.find(
    (item) => item.provider === videoApiProvider
  );

  const saveVideoKeyMutation = useMutation({
    mutationFn: (data: { apiKey: string; provider: string }) =>
      campaignsApi.setVideoApiKey(data.apiKey, data.provider, init),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'video-key-status'] });
      setIsVideoKeyModalOpen(false);
      setVideoApiKeyInput('');
      alert('🔑 ' + (res.message || 'Chave de Vídeo IA configurada com sucesso!'));
    },
    onError: (err: any) => {
      alert('Erro ao salvar chave de vídeo: ' + (err.message || 'Falha na requisição'));
    }
  });

  // Queries
  const briefingQuery = useQuery({
    queryKey: ['campaign', campaign.id, 'briefing'],
    queryFn: () => campaignsApi.getBriefing(campaign.id, init),
    enabled: !!token
  });

  const audiencesQuery = useQuery({
    queryKey: ['campaign', campaign.id, 'audiences'],
    queryFn: () => campaignsApi.getAudiences(campaign.id, init),
    enabled: !!token
  });

  const creativesQuery = useQuery({
    queryKey: ['campaign', campaign.id, 'creatives'],
    queryFn: () => campaignsApi.getCreatives(campaign.id, init),
    enabled: !!token
  });

  // Manter o modal de imagem ampliada/galeria sempre sincronizado com as versões mais recentes
  useEffect(() => {
    if (zoomedCreative && creativesQuery.data?.items) {
      const fresh = creativesQuery.data.items.find(c => c.id === zoomedCreative.id);
      if (fresh) {
        setZoomedCreative(fresh);
        if (!selectedZoomUrl) {
          setSelectedZoomUrl(fresh.assetUrl || null);
        }
      }
    }
  }, [creativesQuery.data]);

  const instagramPostsQuery = useQuery({
    queryKey: ['campaign', campaign.id, 'instagram-posts'],
    queryFn: () => campaignsApi.getInstagramPosts(campaign.id, init),
    enabled: !!token
  });

  const intelligenceQuery = useQuery({
    queryKey: ['campaign', campaign.id, 'intelligence', activeTenantId, token],
    queryFn: () => campaignsApi.getCampaignIntelligence(campaign.id, init),
    enabled: !!token && activeTab === 'intelligence'
  });

  const memoryQuery = useQuery({
    queryKey: ['campaigns', 'intelligence-memory', activeTenantId, token],
    queryFn: () => campaignsApi.getCreativeMemory(init),
    enabled: !!token && activeTab === 'intelligence'
  });

  const analyzeIntelligenceMutation = useMutation({
    mutationFn: () => campaignsApi.analyzeCampaignIntelligence(campaign.id, init),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'intelligence'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'intelligence-memory'] });
      const errors = [res.meta?.error, res.google?.error].filter(Boolean);
      alert(`${res.totalAnalyzed} itens analisados para retroalimentar as próximas propostas.${errors.length ? `\nColetas pendentes: ${errors.join(' · ')}` : ''}`);
    },
    onError: (err: any) => {
      alert(`⚠️ Erro na análise de inteligência: ${err.message}`);
    }
  });

  const scheduleInstagramMutation = useMutation({
    mutationFn: () => campaignsApi.scheduleInstagramSequence(campaign.id, { addCommentTrigger }, init),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'instagram-posts'] });
      alert(`✅ Sucesso! Agendada sequência de ${res.totalScheduled} posts no Instagram nos horários 07:00, 13:00 e 19:00 (Horário de Brasília) a partir de agora.`);
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao agendar no Instagram: ${err.message || 'Falha na requisição'}`);
    }
  });

  const clearScheduleMutation = useMutation({
    mutationFn: () => campaignsApi.clearInstagramSchedule(campaign.id, init),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'instagram-posts'] });
      alert(`🗑️ Grade limpa com sucesso! ${res.deletedCount || 0} agendamento(s) pendente(s) removido(s).`);
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao limpar grade: ${err.message || 'Falha na requisição'}`);
    }
  });

  const publishCarouselMutation = useMutation({
    mutationFn: (customCaption?: string) =>
      campaignsApi.publishInstagramCarousel(campaign.id, {
        addCommentTrigger,
        caption: typeof customCaption === 'string' && customCaption.trim() ? customCaption.trim() : undefined
      }, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'instagram-posts'] });
      setIsCarouselModalOpen(false);
      alert('🚀 Carrossel com as 5 artes publicado com sucesso no Feed do Instagram!');
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao publicar carrossel no Instagram: ${err.message}`);
    }
  });

  const publishStoriesMutation = useMutation({
    mutationFn: (payload?: { creativeId?: string; stepOrder?: number; imageUrl?: string; caption?: string }) =>
      campaignsApi.publishInstagramStories(campaign.id, payload, init),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'instagram-posts'] });
      setIsStoryModalOpen(false);
      alert(`⚡ Arte do Step ${res.stepOrder || storySelectedStep} publicada com sucesso nos Stories do Instagram!`);
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao publicar Stories: ${err.message}`);
    }
  });

  // Mutations
  const transitionMutation = useMutation({
    mutationFn: (status: string) => campaignsApi.transition(campaign.id, status, init, { publish: false }),
    onSuccess: (_res: any, variables: string) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      setCurrentStatus(variables);

      if (variables === 'pausada') {
        toast.success('⏸️ Campanha pausada com sucesso.');
      } else if (variables === 'ativa') {
        toast.success('▶️ Campanha ativada / retomada com sucesso!');
      } else if (variables === 'em_revisao') {
        toast.success('🔍 Campanha enviada para revisão de criativos.');
      } else if (variables === 'pronta_publicacao') {
        toast.success('Revisão aprovada. Agora publique em cada canal na etapa 3.');
      } else {
        toast.success(`Status da campanha atualizado para ${variables}`);
      }
    },
    onError: (err: any) => {
      console.error('[transitionMutation] Error:', err);
      toast.error(`⚠️ Erro ao atualizar status: ${err.message || 'Falha na comunicação com o servidor'}`);
    }
  });

  const updateBudgetMutation = useMutation({
    mutationFn: (daily: number) => campaignsApi.updateBriefing(campaign.id, { budgetDaily: daily }, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'briefing'] });
      setIsEditingBudget(false);
      toast.success('💰 Orçamento diário atualizado com sucesso!');
    },
    onError: (err: any) => {
      toast.error(`Erro ao atualizar orçamento: ${err.message || 'Falha na requisição'}`);
    }
  });

  const upgradeMCAMutation = useMutation({
    mutationFn: (data?: Partial<MCAGeneratePayload>) => campaignsApi.upgradeMCA(campaign.id, data || {}, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'briefing'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'audiences'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      queryClient.invalidateQueries({ queryKey: ['ai-balance'] });
      queryClient.invalidateQueries({ queryKey: ['ai-packages'] });
      queryClient.invalidateQueries({ queryKey: ['ai-cost'] });
      queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
      setActiveTab('strategy');
      setIsUpgradeModalOpen(false);
    },
    onError: (error: Error) => toast.error(error.message || 'Não foi possível gerar as propostas.'),
  });

  const updateCreativeMutation = useMutation({
    mutationFn: ({ creativeId, data }: { creativeId: string, data: Partial<CampaignCreative> }) => 
      campaignsApi.updateCreative(campaign.id, creativeId, data, init),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      setEditingCreative(null);
      if (res?.metaSync?.synced) {
        alert('✅ Copy salva no CRM e sincronizada com sucesso na Meta Ads!');
      } else {
        alert('💾 Copy salva com sucesso no CRM!');
      }
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao salvar copy: ${err.message}`);
    }
  });

  const syncMetaAdsMutation = useMutation({
    mutationFn: (options: { recreateAds?: boolean } | void = {}) => campaignsApi.syncMetaAds(campaign.id, options || undefined, init),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      const recreatedCount = (res.details || []).filter((d: any) => d.recreated).length;
      if (recreatedCount > 0) {
        alert(`🚀 Sincronização e Republicação Meta Ads concluída!\n\n${res.syncedCount} de ${res.total} anúncios sincronizados com sucesso.\n(${recreatedCount} anúncio(s) foram recriados na Meta Ads).`);
      } else {
        alert(`🚀 Sincronização com a Meta concluída!\n\n${res.syncedCount} de ${res.total} anúncios atualizados com sucesso na sua conta da Meta Ads.`);
      }
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao sincronizar com a Meta Ads: ${err.message}`);
    }
  });

  const syncGoogleAdsMutation = useMutation({
    mutationFn: (options: { autoActivate?: boolean; recreate?: boolean } | void = {}) =>
      campaignsApi.syncGoogleAds(campaign.id, options || undefined, init),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      alert(`Conteúdo enviado ao Google Ads: títulos, descrições, palavras-chave e destino.\n\nConta: ${res.customerId}\nCampanha: ${res.campaignId}\nGrupo: ${res.adGroupId}`);
    },
    onError: (err: any) => {
      alert(`⚠️ Erro ao sincronizar com Google Ads: ${err.message}`);
    }
  });

  const generateImageMutation = useMutation({
    mutationFn: async (payload: string | { creativeId: string; format?: 'feed' | 'stories' }) => {
      const creativeId = typeof payload === 'string' ? payload : payload.creativeId;
      const options = typeof payload === 'object' && payload.format ? { format: payload.format } : undefined;
      const result = await campaignsApi.generateCreativeImage(campaign.id, creativeId, options, init);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['ai-balance'] });
      queryClient.invalidateQueries({ queryKey: ['ai-packages'] });
      queryClient.invalidateQueries({ queryKey: ['ai-cost'] });
      queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
    },
    onError: (err: any) => {
      alert('Erro ao sintetizar arte: ' + (err.message || 'Falha no provedor de IA'));
    }
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ creativeId, base64 }: { creativeId: string, base64: string }) => 
      campaignsApi.uploadCreativeImage(campaign.id, creativeId, base64, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => {
      alert('Erro ao fazer upload da imagem: ' + (err.message || 'Falha no upload'));
    }
  });

  const restoreImageMutation = useMutation({
    mutationFn: ({ creativeId, assetUrl }: { creativeId: string, assetUrl: string }) => 
      campaignsApi.restoreCreativeImage(campaign.id, creativeId, assetUrl, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => {
      alert('Erro ao restaurar imagem anterior: ' + (err.message || 'Falha na restauração'));
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: ({ creativeId, assetUrl }: { creativeId: string, assetUrl?: string }) => 
      campaignsApi.deleteCreativeImage(campaign.id, creativeId, assetUrl, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => {
      alert('Erro ao apagar imagem do R2: ' + (err.message || 'Falha na exclusão'));
    }
  });

  const notifyWhatsAppMutation = useMutation({
    mutationFn: () => campaignsApi.notifyAdminWhatsApp(campaign.id, init),
    onSuccess: (res) => {
      if (res.ok) {
        alert(`✅ Propostas enviadas com sucesso no WhatsApp do Admin (${res.adminPhone})!`);
      } else {
        alert(`Aviso: Notificação gerada no sistema. (Motivo: ${res.result?.reason || 'WhatsApp em configuração'})`);
      }
    },
    onError: (err: any) => {
      alert('Erro ao enviar alerta no WhatsApp: ' + (err.message || 'Falha na requisição'));
    }
  });

  const previewMoviePromptMutation = useMutation({
    mutationFn: (creativeId: string) => campaignsApi.previewCreativeVideoPrompt(
      campaign.id,
      creativeId,
      { format: 'vertical', duration: videoDuration },
      init
    ),
    onSuccess: (data) => setVideoPrompt(data.prompt),
    onError: (err: any) => alert('Erro ao preparar prompt: ' + (err.message || 'Falha na requisição'))
  });

  const openMovieGeneration = (creativeId: string) => {
    setPendingVideoCreativeId(creativeId);
    setVideoPrompt('');
    const activeProvider = videoKeyQuery.data?.providers?.find(p => p.configured)?.provider;
    if (activeProvider) {
      setVideoGenerationProvider(activeProvider);
    }
    setIsVideoGenerationModalOpen(true);
    previewMoviePromptMutation.mutate(creativeId);
  };

  // Geração de Filme Real via IA Generativa (Kling AI / Minimax / Luma / Fal.ai)
  const generateMovieMutation = useMutation({
    mutationFn: async ({ creativeId, format = 'vertical', provider, prompt }: { creativeId: string; format?: 'vertical' | 'square'; provider: VideoProvider; prompt: string }) => {
      return await campaignsApi.generateCreativeVideo(campaign.id, creativeId, { format, provider, prompt, duration: videoDuration }, init);
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-balance'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-packages'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-cost'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
      const updatedList = await campaignsApi.getCreatives(campaign.id, init);
      const updated = updatedList.items.find(c => c.id === variables.creativeId);
      if (updated) setZoomedCreative(updated);
      setZoomMediaTab('video');
      setIsVideoGenerationModalOpen(false);
      setPendingVideoCreativeId(null);
    },
    onError: (err: any) => {
      const msg = err?.message || '';
      if (msg.includes('chave') || msg.includes('key') || msg.includes('Fal.ai') || msg.includes('Replicate')) {
        setVideoApiProvider(videoGenerationProvider);
        setIsVideoKeyModalOpen(true);
      }
      alert('Aviso de Vídeo IA: ' + msg);
    }
  });

  // Geração de Vídeo Motion Express (Canvas 4-Atos com Legendas Animadas no Navegador)
  const generateVideoMutation = useMutation({
    mutationFn: async ({ creativeId, format = 'vertical', gender }: { creativeId: string; format?: 'vertical' | 'square'; gender?: 'male' | 'female' | 'auto' }) => {
      const cr = creativesQuery.data?.items?.find(c => c.id === creativeId);
      if (!cr) throw new Error('Criativo não encontrado');

      // Tentar síntese nativa de cena completa com o Avatar em atuação
      try {
        const briefingData = briefingQuery.data;
        const avatarsList = await campaignsApi.getTenantAvatars(init).catch(() => ({ items: [] }));
        let avatarData = null;
        if (avatarsList.items?.length > 0) {
          const pref = gender || briefingData?.avatarGenderPreference || 'auto';
          if (pref === 'male') avatarData = avatarsList.items.find(a => a.gender === 'male');
          else if (pref === 'female') avatarData = avatarsList.items.find(a => a.gender === 'female');
          else avatarData = (cr.stepOrder && cr.stepOrder % 2 === 1) ? avatarsList.items.find(a => a.gender === 'female') : avatarsList.items.find(a => a.gender === 'male');
          
          if (!avatarData) avatarData = avatarsList.items[0];
        }

        let parsedScenes: any[] = [];
        if ((cr as any).videoScenes) {
          try {
            parsedScenes = typeof (cr as any).videoScenes === 'string' ? JSON.parse((cr as any).videoScenes) : (cr as any).videoScenes;
          } catch (_) {}
        }

        const videoBlob = await synthesizeCreativeVideoBlob({
          assetUrl: cr.assetUrl || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1080',
          headline: cr.headline,
          angleName: cr.angleName || cr.name,
          ctaText: cr.callToActionType || 'SAIBA MAIS',
          avatarUrl: avatarData?.asset_url,
          avatarName: avatarData?.name || (gender === 'female' ? 'Dra. Especialista' : 'Dr. Especialista'),
          avatarRole: avatarData?.role_title || 'Porta-voz Oficial',
          format,
          durationSeconds: videoDuration,
          videoScript: (cr as any).videoScript || undefined,
          videoScenes: parsedScenes.length > 0 ? parsedScenes : undefined,
          narrationAudioUrl: (cr as any).videoNarrationUrl || undefined
        });

        // Enviar os bytes do vídeo diretamente para o Cloudflare R2
        return await campaignsApi.uploadCreativeVideo(campaign.id, creativeId, videoBlob, init);
      } catch (clientErr: any) {
        console.warn('[VideoSynthesizer] Client synthesis fallback to API:', clientErr?.message);
        return await campaignsApi.generateCreativeVideo(campaign.id, creativeId, { format, duration: videoDuration }, init);
      }
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-balance'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-packages'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-cost'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
      const updatedList = await campaignsApi.getCreatives(campaign.id, init);
      const updated = updatedList.items.find(c => c.id === variables.creativeId);
      if (updated) {
        setZoomedCreative(updated);
      }
      setZoomMediaTab('video');
    },
    onError: (err: any) => {
      alert('Erro ao sintetizar vídeo: ' + (err.message || 'Falha na geração de vídeo'));
    }
  });

  // isSynthesizingAll and synthProgress already declared at top of component

  const handleSynthesizeAll = async () => {
    const list = creativesQuery.data?.items || [];
    if (list.length === 0) return;

    setIsSynthesizingAll(true);
    let successCount = 0;
    try {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        setSynthProgress(`Sintetizando ${i + 1}/${list.length}: ${item.angleName || item.headline}...`);
        try {
          await campaignsApi.generateCreativeImage(campaign.id, item.id, init);
          successCount++;
        } catch (itemErr: any) {
          console.warn(`[Synthesize Lote] Erro no criativo ${item.id}:`, itemErr?.message);
        }
        await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      // Ao concluir a síntese do lote de artes, envia o alerta formatado no WhatsApp do Admin
      if (successCount > 0) {
        setSynthProgress('Enviando resumo dos anúncios para o WhatsApp do Admin...');
        await campaignsApi.notifyAdminWhatsApp(campaign.id, init).catch(e => console.warn('WhatsApp alert error:', e));
      }
    } catch (err: any) {
      alert('Erro durante o processamento do lote: ' + (err.message || 'Falha na requisição'));
    } finally {
      setIsSynthesizingAll(false);
      setSynthProgress('');
    }
  };

  const handleFileUpload = (creativeId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        uploadImageMutation.mutate({ creativeId, base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadVideoMutation = useMutation({
    mutationFn: ({ creativeId, file }: { creativeId: string; file: File }) =>
      campaignsApi.uploadCreativeVideo(campaign.id, creativeId, file, init),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      alert('✅ Vídeo enviado e vinculado ao criativo com sucesso!');
    },
    onError: (err: any) => {
      alert('Erro ao fazer upload do vídeo: ' + (err.message || 'Falha no upload'));
    }
  });

  const handleVideoUpload = (creativeId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadVideoMutation.mutate({ creativeId, file });
    }
  };

  const handleApproveStrategy = () => {
    const creativesList = creativesQuery.data?.items || [];
    const allCreativesHaveImage = creativesList.length > 0 && creativesList.every(cr => !!(cr.assetUrl || cr.videoUrl));
    
    if (!allCreativesHaveImage) {
      alert('Por favor, adicione ou sintetize uma imagem para todos os criativos antes de aprovar a estratégia.');
      return;
    }
    transitionMutation.mutate('pronta_publicacao');
  };

  const handleExecuteUpgrade = (e: React.FormEvent) => {
    e.preventDefault();
    upgradeMCAMutation.mutate({
      market_niche: niche,
      pain_points: painPoints,
      tone,
      regenerate_images: !keepImagesOnUpgrade
    });
  };

  const briefing = briefingQuery.data;
  const audiences = audiencesQuery.data?.items || [];
  const creatives = creativesQuery.data?.items || [];

  const reviewCreatives = creatives.filter(creative => creativeReviewLevel(creative) === reviewLevel);
  const previewCreatives = activeTab === 'strategy' ? reviewCreatives : creatives;
  const selectedPreviewCreative = previewCreatives.find(c => c.id === previewCreativeId) || previewCreatives[0];

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      zIndex: 9999, 
      display: 'flex', 
      justifyContent: isExpanded ? 'center' : 'flex-end', 
      alignItems: isExpanded ? 'center' : 'stretch', 
      padding: isExpanded ? '1.25rem' : '0',
      background: isLight ? 'rgba(15, 23, 42, 0.45)' : 'rgba(0,0,0,0.75)', 
      backdropFilter: 'blur(8px)',
      transition: 'all 0.25s ease'
    }}>
      {/* Click outside to close */}
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} />
      
      <div className="drawer-panel animate-in" style={{ 
        position: 'relative', 
        width: isExpanded ? '96vw' : '100%', 
        maxWidth: isExpanded ? '1680px' : '780px', 
        height: isExpanded ? '94vh' : '100%',
        background: isLight ? '#ffffff' : '#0e0e11', 
        borderRadius: isExpanded ? '20px' : '0',
        border: isLight 
          ? (isExpanded ? '1px solid #cbd5e1' : 'none') 
          : (isExpanded ? '1px solid rgba(255,255,255,0.12)' : 'none'),
        borderLeft: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
        display: 'flex', 
        flexDirection: 'column', 
        boxShadow: isLight 
          ? (isExpanded ? '0 24px 64px rgba(15,23,42,0.15), 0 0 0 1px rgba(0,0,0,0.05)' : '-8px 0 32px rgba(15,23,42,0.12)')
          : (isExpanded ? '0 24px 64px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05)' : '-8px 0 32px rgba(0,0,0,0.5)'),
        overflow: 'hidden',
        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
      }}>
        
        {/* Header */}
        <div style={{ 
          padding: '1.25rem 1.75rem', 
          borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)' 
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', color: isLight ? '#4f46e5' : 'var(--primary-color)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Campanhas de anúncios
              </span>
              <CampaignStatusBadge campaign={{ ...campaign, status: currentStatus }} />
              <DeleteCampaignDraftButton campaign={campaign} init={init} onDeleted={onClose} />
              {isExpanded && (
                <span style={{ 
                  fontSize: '0.7rem', 
                  color: isLight ? '#059669' : '#10b981', 
                  background: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.12)', 
                  border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16, 185, 129, 0.3)', 
                  padding: '0.15rem 0.5rem', 
                  borderRadius: '6px', 
                  fontWeight: 600 
                }}>
                  Modo Tela Cheia Expandido
                </span>
              )}
            </div>

            {isEditingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.2rem' }}>
                <input
                  type="text"
                  value={campaignNameInput}
                  onChange={(e) => setCampaignNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && campaignNameInput.trim()) {
                      updateCampaignNameMutation.mutate(campaignNameInput.trim());
                    } else if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setCampaignNameInput(campaign.name || '');
                    }
                  }}
                  autoFocus
                  style={{
                    fontSize: isExpanded ? '1.25rem' : '1.1rem',
                    fontWeight: 700,
                    padding: '0.3rem 0.6rem',
                    borderRadius: '8px',
                    border: '2px solid var(--primary-color)',
                    background: isLight ? '#ffffff' : '#1c1c22',
                    color: isLight ? '#0f172a' : '#fff',
                    minWidth: '280px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (campaignNameInput.trim()) {
                      updateCampaignNameMutation.mutate(campaignNameInput.trim());
                    }
                  }}
                  disabled={updateCampaignNameMutation.isPending || !campaignNameInput.trim()}
                  style={{
                    background: '#10b981',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    padding: '0.45rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Salvar Nome"
                >
                  {updateCampaignNameMutation.isPending ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingName(false);
                    setCampaignNameInput(campaign.name || '');
                  }}
                  style={{
                    background: 'transparent',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: isLight ? '#64748b' : '#94a3b8',
                    padding: '0.45rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Cancelar"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h2 style={{ fontSize: isExpanded ? '1.45rem' : '1.25rem', margin: 0, color: isLight ? '#0f172a' : 'var(--text-color)', fontWeight: 700 }}>
                  {campaign.name}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsEditingName(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isLight ? '#64748b' : '#94a3b8',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Editar nome da campanha"
                >
                  <Edit3 size={16} />
                </button>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <CampaignChannelActions campaign={campaign}
              metaPending={syncMetaAdsMutation.isPending} googlePending={syncGoogleAdsMutation.isPending}
              onSyncMeta={() => syncMetaAdsMutation.mutate({ recreateAds: true })}
              onSyncGoogle={() => syncGoogleAdsMutation.mutate({ autoActivate: false })} />
            {channels.includes('meta') && <button
              onClick={() => setIsUpgradeModalOpen(true)}
              className="primary-button"
              style={{ padding: '0.5rem 0.9rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'linear-gradient(135deg, #6366f1, #a855f7)', border: 'none', color: '#fff' }}
              title="Reestruturar com 5 Níveis de Consciência"
            >
              <Sparkles size={15} /> Regerar propostas Meta
            </button>}
            <button 
              onClick={() => setIsExpanded(!isExpanded)} 
              style={{ 
                background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)', 
                border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                color: isLight ? '#475569' : 'var(--text-muted)', 
                cursor: 'pointer', 
                padding: '0.5rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={isExpanded ? 'Reduzir para Gaveta Lateral' : 'Expandir Tela Inteira'}
            >
              {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button 
              onClick={onClose} 
              style={{ 
                background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)', 
                border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                color: isLight ? '#475569' : 'var(--text-muted)', 
                cursor: 'pointer', 
                padding: '0.5rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Fechar"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* BARRA DE CICLO DE VIDA DA CAMPANHA (LIFECYCLE CONTROL BAR) */}
        <div style={{
          background: isLight ? '#f8fafc' : '#0d0d12',
          borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          padding: '0.75rem 1.75rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Status Badge & Orçamento Diário */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ciclo de Vida:
            </span>
            <CampaignStatusBadge campaign={{ ...campaign, status: currentStatus }} />

            {/* Pílula de Orçamento Diário com Edição Rápida */}
            {isEditingBudget ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{ fontSize: '0.8rem', color: isLight ? '#475569' : '#cbd5e1', fontWeight: 600 }}>R$</span>
                <input
                  type="number"
                  min={10}
                  step={5}
                  value={budgetDailyInput}
                  onChange={(e) => setBudgetDailyInput(Number(e.target.value))}
                  style={{
                    width: '80px',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    borderRadius: '6px',
                    border: '1px solid var(--primary-color)',
                    background: isLight ? '#fff' : '#1c1c22',
                    color: isLight ? '#0f172a' : '#fff'
                  }}
                  autoFocus
                />
                <span style={{ fontSize: '0.8rem', color: isLight ? '#475569' : '#cbd5e1' }}>/dia</span>
                <button
                  type="button"
                  onClick={() => updateBudgetMutation.mutate(budgetDailyInput)}
                  disabled={updateBudgetMutation.isPending}
                  style={{
                    background: '#10b981',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '0.25rem 0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}
                >
                  {updateBudgetMutation.isPending ? '...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingBudget(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isLight ? '#64748b' : '#94a3b8',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => setIsEditingBudget(true)}
                title="Clique para ajustar o orçamento diário da campanha"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '20px',
                  background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: isLight ? '#0f172a' : '#f8fafc',
                  transition: 'all 0.15s ease'
                }}
              >
                <DollarSign size={13} style={{ color: '#10b981' }} />
                <span>R$ {budgetDailyInput}/dia</span>
                <Edit3 size={11} style={{ opacity: 0.6 }} />
              </div>
            )}
          </div>

          {/* Status de Conexão com a Meta & Ações Globais de Veiculação */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <CampaignChannelSummary campaign={campaign} />

            {/* Ações globais de veiculação (Pausar / Retomar) se a campanha já foi veiculada */}
            {['ativa', 'running', 'active', 'publicada'].includes(currentStatus) && (
              <button
                type="button"
                onClick={() => transitionMutation.mutate('pausada')}
                disabled={transitionMutation.isPending}
                className="secondary-button"
                style={{
                  fontSize: '0.78rem',
                  padding: '0.35rem 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  borderColor: '#f59e0b',
                  color: isLight ? '#d97706' : '#fbbf24',
                  background: isLight ? '#fffbeb' : 'rgba(245,158,11,0.08)'
                }}
                title="Pausar a veiculação desta campanha temporariamente"
              >
                <Pause size={13} />
                Pausar
              </button>
            )}

            {['pausada', 'paused'].includes(currentStatus) && (
              <button
                type="button"
                onClick={() => transitionMutation.mutate('ativa')}
                disabled={transitionMutation.isPending}
                className="primary-button"
                style={{
                  fontSize: '0.78rem',
                  padding: '0.35rem 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  background: '#10b981',
                  border: 'none',
                  color: '#fff',
                  fontWeight: 700
                }}
                title="Retomar a veiculação nos canais vinculados"
              >
                <Play size={13} />
                Retomar
              </button>
            )}
          </div>
        </div>

        {/* Tabs Segmentadas de Alto Contraste */}
        <div style={{ 
          display: 'flex', 
          borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', 
          padding: '0.75rem 1.75rem', 
          background: isLight ? '#f1f5f9' : '#13131a', 
          gap: '0.6rem',
          overflowX: 'auto',
          alignItems: 'center'
        }}>
          {/* Tab 1: Briefing */}
          <button 
            onClick={() => setActiveTab('briefing')}
            style={{
              padding: '0.65rem 1.1rem',
              cursor: 'pointer',
              fontWeight: activeTab === 'briefing' ? 700 : 500,
              fontSize: '0.85rem',
              borderRadius: '10px',
              color: activeTab === 'briefing' ? (isLight ? '#312e81' : '#ffffff') : (isLight ? '#475569' : '#94a3b8'),
              background: activeTab === 'briefing' 
                ? (isLight ? '#e0e7ff' : 'linear-gradient(135deg, rgba(99,102,241,0.35), rgba(79,70,229,0.3))') 
                : (isLight ? '#ffffff' : 'rgba(255,255,255,0.03)'),
              border: activeTab === 'briefing' 
                ? (isLight ? '1px solid #6366f1' : '1px solid #818cf8') 
                : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.07)'),
              boxShadow: activeTab === 'briefing' 
                ? (isLight ? '0 2px 8px rgba(99,102,241,0.2)' : '0 4px 14px rgba(99,102,241,0.35)') 
                : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.18s ease'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              background: activeTab === 'briefing' ? '#6366f1' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'briefing' ? '#fff' : (isLight ? '#475569' : '#94a3b8'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              1
            </div>
            <Target size={15} style={{ color: activeTab === 'briefing' ? (isLight ? '#4338ca' : '#a5b4fc') : '#64748b' }} />
            <span>Briefing da campanha</span>
          </button>

          {/* Tab 2: Criativos & Anúncios */}
          <button 
            onClick={() => setActiveTab('strategy')}
            style={{
              padding: '0.65rem 1.1rem',
              cursor: 'pointer',
              fontWeight: activeTab === 'strategy' ? 700 : 500,
              fontSize: '0.85rem',
              borderRadius: '10px',
              color: activeTab === 'strategy' ? (isLight ? '#581c87' : '#ffffff') : (isLight ? '#475569' : '#94a3b8'),
              background: activeTab === 'strategy' 
                ? (isLight ? '#f3e8ff' : 'linear-gradient(135deg, rgba(168,85,247,0.35), rgba(147,51,234,0.3))') 
                : (isLight ? '#ffffff' : 'rgba(255,255,255,0.03)'),
              border: activeTab === 'strategy' 
                ? (isLight ? '1px solid #a855f7' : '1px solid #c084fc') 
                : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.07)'),
              boxShadow: activeTab === 'strategy' 
                ? (isLight ? '0 2px 8px rgba(168,85,247,0.2)' : '0 4px 14px rgba(168,85,247,0.35)') 
                : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.18s ease'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              background: activeTab === 'strategy' ? '#a855f7' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'strategy' ? '#fff' : (isLight ? '#475569' : '#94a3b8'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              2
            </div>
            <Megaphone size={15} style={{ color: activeTab === 'strategy' ? (isLight ? '#7e22ce' : '#d8b4fe') : '#64748b' }} />
            <span>Revisar anúncios</span>
            <span style={{
              background: activeTab === 'strategy' ? (isLight ? '#e9d5ff' : 'rgba(255,255,255,0.25)') : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'strategy' ? (isLight ? '#6b21a8' : '#fff') : (isLight ? '#64748b' : '#94a3b8'),
              padding: '0.1rem 0.45rem',
              borderRadius: '12px',
              fontSize: '0.72rem',
              fontWeight: 700,
              marginLeft: '0.2rem'
            }}>
              {channels.includes('meta') ? `${creatives.length} artes` : `${campaignTextList(campaign.google_headlines).length} títulos`}
            </span>
          </button>

          {/* Tab 3: Estúdio de Vídeo IA (Studio Flow) */}
          <button 
            onClick={() => setActiveTab('video-studio')}
            style={{
              padding: '0.65rem 1.1rem',
              cursor: 'pointer',
              fontWeight: activeTab === 'video-studio' ? 700 : 500,
              fontSize: '0.85rem',
              borderRadius: '10px',
              color: activeTab === 'video-studio' ? (isLight ? '#1e3a8a' : '#ffffff') : (isLight ? '#475569' : '#94a3b8'),
              background: activeTab === 'video-studio' 
                ? (isLight ? '#dbeafe' : 'linear-gradient(135deg, rgba(59,130,246,0.35), rgba(37,99,235,0.3))') 
                : (isLight ? '#ffffff' : 'rgba(255,255,255,0.03)'),
              border: activeTab === 'video-studio' 
                ? (isLight ? '1px solid #3b82f6' : '1px solid #60a5fa') 
                : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.07)'),
              boxShadow: activeTab === 'video-studio' 
                ? (isLight ? '0 2px 8px rgba(59,130,246,0.2)' : '0 4px 14px rgba(59,130,246,0.35)') 
                : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.18s ease'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              background: activeTab === 'video-studio' ? '#3b82f6' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'video-studio' ? '#fff' : (isLight ? '#475569' : '#94a3b8'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              🎬
            </div>
            <Film size={15} style={{ color: activeTab === 'video-studio' ? (isLight ? '#1d4ed8' : '#93c5fd') : '#64748b' }} />
            <span>Estúdio de Vídeo</span>
            <span style={{
              background: activeTab === 'video-studio' ? (isLight ? '#bfdbfe' : 'rgba(255,255,255,0.25)') : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'video-studio' ? (isLight ? '#1e40af' : '#fff') : (isLight ? '#64748b' : '#94a3b8'),
              padding: '0.1rem 0.45rem',
              borderRadius: '12px',
              fontSize: '0.72rem',
              fontWeight: 700,
              marginLeft: '0.2rem'
            }}>
              Veo 3.1
            </span>
          </button>

          {/* Tab 4: Publicação & Canais */}
          <button 
            onClick={() => setActiveTab('instagram')}
            style={{
              padding: '0.65rem 1.1rem',
              cursor: 'pointer',
              fontWeight: activeTab === 'instagram' ? 700 : 500,
              fontSize: '0.85rem',
              borderRadius: '10px',
              color: activeTab === 'instagram' ? (isLight ? '#831843' : '#ffffff') : (isLight ? '#475569' : '#94a3b8'),
              background: activeTab === 'instagram' 
                ? (isLight ? '#fce7f3' : 'linear-gradient(135deg, rgba(236,72,153,0.35), rgba(219,39,119,0.3))') 
                : (isLight ? '#ffffff' : 'rgba(255,255,255,0.03)'),
              border: activeTab === 'instagram' 
                ? (isLight ? '1px solid #ec4899' : '1px solid #f472b6') 
                : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.07)'),
              boxShadow: activeTab === 'instagram' 
                ? (isLight ? '0 2px 8px rgba(236,72,153,0.2)' : '0 4px 14px rgba(236,72,153,0.35)') 
                : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.18s ease'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              background: activeTab === 'instagram' ? '#ec4899' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'instagram' ? '#fff' : (isLight ? '#475569' : '#94a3b8'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              3
            </div>
            <Layers size={15} style={{ color: activeTab === 'instagram' ? (isLight ? '#be185d' : '#f9a8d4') : '#64748b' }} />
            <span>Aprovar e publicar</span>
            <span style={{
              background: activeTab === 'instagram' ? (isLight ? '#fbcfe8' : 'rgba(255,255,255,0.25)') : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'instagram' ? (isLight ? '#9d174d' : '#fff') : (isLight ? '#64748b' : '#94a3b8'),
              padding: '0.1rem 0.45rem',
              borderRadius: '12px',
              fontSize: '0.72rem',
              fontWeight: 700,
              marginLeft: '0.2rem'
            }}>
              {channels.map(channel => channel === 'meta' ? 'Meta' : 'Google').join(' • ')}
            </span>
          </button>

          {/* Tab 4: Inteligência */}
          <button 
            onClick={() => setActiveTab('intelligence')}
            style={{
              padding: '0.65rem 1.1rem',
              cursor: 'pointer',
              fontWeight: activeTab === 'intelligence' ? 700 : 500,
              fontSize: '0.85rem',
              borderRadius: '10px',
              color: activeTab === 'intelligence' ? (isLight ? '#064e3b' : '#ffffff') : (isLight ? '#475569' : '#94a3b8'),
              background: activeTab === 'intelligence' 
                ? (isLight ? '#d1fae5' : 'linear-gradient(135deg, rgba(16,185,129,0.35), rgba(5,150,105,0.3))') 
                : (isLight ? '#ffffff' : 'rgba(255,255,255,0.03)'),
              border: activeTab === 'intelligence' 
                ? (isLight ? '1px solid #10b981' : '1px solid #34d399') 
                : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.07)'),
              boxShadow: activeTab === 'intelligence' 
                ? (isLight ? '0 2px 8px rgba(16,185,129,0.2)' : '0 4px 14px rgba(16,185,129,0.35)') 
                : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.18s ease'
            }}
          >
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              background: activeTab === 'intelligence' ? '#10b981' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'),
              color: activeTab === 'intelligence' ? '#fff' : (isLight ? '#475569' : '#94a3b8'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              4
            </div>
            <Award size={15} style={{ color: activeTab === 'intelligence' ? (isLight ? '#047857' : '#6ee7b7') : '#64748b' }} />
            <span>Acompanhar resultados</span>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isExpanded ? '2rem' : '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Banner de Upgrade para campanhas legadas sem 5 criativos */}
          {((channels.includes('meta') && creatives.length < 5) || (channels.includes('google') && campaignTextList(campaign.google_headlines).length < 3)) && (
            <div style={{ background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '12px', padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div>
                <strong style={{ color: '#818cf8', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
                  <Sparkles size={16} /> Prepare os anúncios para os canais selecionados
                </strong>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
                  Gere as propostas a partir do briefing e revise o conteúdo antes de publicar.
                </p>
              </div>
              <button 
                onClick={() => channels.includes('meta') ? upgradeMCAMutation.mutate({}) : googleProposals.generate()}
                disabled={upgradeMCAMutation.isPending || googleProposals.generating}
                className="primary-button" 
                style={{ padding: '0.6rem 1rem', fontSize: '0.85rem', whiteSpace: 'nowrap', display: 'flex', gap: '0.4rem', alignItems: 'center' }}
              >
                {upgradeMCAMutation.isPending ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                {upgradeMCAMutation.isPending || googleProposals.generating ? 'Gerando propostas...' : 'Gerar propostas dos anúncios'}
              </button>
            </div>
          )}

          {activeTab === 'strategy' && (
            <CampaignReviewTabs key={campaign.id} channels={channels} creatives={creatives} level={reviewLevel}
              onLevelChange={setReviewLevel} googleReview={<GoogleSearchReview campaign={googleProposals.campaign} onGenerate={googleProposals.generate}
              generating={googleProposals.generating} error={googleProposals.error} generated={googleProposals.generated}
              onSave={googleProposals.save} saving={googleProposals.saving} saveError={googleProposals.saveError} init={init} />}>
            <div style={{ width: '100%' }}>
              {creativesQuery.isLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <Loader2 size={32} className="spin" style={{ margin: '0 auto 1rem' }} />
                  <p>Carregando matriz de criativos...</p>
                </div>
              ) : creatives.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: 'var(--text-muted)', border: isLight ? '1px dashed #cbd5e1' : '1px dashed var(--border-color)', borderRadius: '16px', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)' }}>
                  <Brain size={48} style={{ opacity: 0.8, margin: '0 auto 1rem', color: '#6366f1' }} />
                  <h4 style={{ color: isLight ? '#0f172a' : '#fff', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 700 }}>Briefing Validado • Estruturação de Cópias</h4>
                  <p style={{ fontSize: '0.88rem', marginBottom: '1.5rem', maxWidth: '560px', marginInline: 'auto', lineHeight: '1.5', color: isLight ? '#475569' : 'var(--text-muted)' }}>
                    Clique abaixo para gerar as propostas de cópias e ganchos dos 5 níveis de consciência (Eugene Schwartz). <strong>Você poderá revisar e editar cada headline, texto e metáfora visual antes de sintetizar as artes.</strong>
                  </p>
                  <button 
                    onClick={() => upgradeMCAMutation.mutate({})}
                    disabled={upgradeMCAMutation.isPending}
                    className="primary-button"
                    style={{ margin: '0 auto', display: 'flex', gap: '0.5rem', alignItems: 'center', background: 'linear-gradient(135deg, #6366f1, #a855f7)', padding: '0.75rem 1.5rem', fontWeight: 700, borderRadius: '8px', border: 'none', color: '#fff', boxShadow: '0 4px 14px rgba(99,102,241,0.25)', cursor: 'pointer' }}
                  >
                    {upgradeMCAMutation.isPending ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
                    {upgradeMCAMutation.isPending ? 'Estruturando Ângulos...' : 'Estruturar Proposta de Textos (5 Ângulos) ✍️'}
                  </button>
                </div>
              ) : reviewCreatives.length === 0 ? (
                <p className="campaign-review-empty-level">Ainda não há propostas neste nível. Selecione outro nível ou gere as propostas Meta.</p>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isExpanded ? 'minmax(380px, 480px) 1fr' : '1fr', 
                  gap: '2rem', 
                  alignItems: 'start' 
                }}>
                  {/* Coluna 1: Preview Sticky do Feed */}
                  {selectedPreviewCreative && (
                    <div style={{ 
                      position: isExpanded ? 'sticky' : 'static', 
                      top: isExpanded ? '0' : 'auto',
                      zIndex: 10 
                    }}>
                      <div style={{ 
                        background: isLight ? '#f8fafc' : '#121218', 
                        border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', 
                        borderRadius: '16px', 
                        padding: '1.25rem',
                        boxShadow: isLight ? '0 4px 16px rgba(15,23,42,0.06)' : 'none'
                      }}>
                      {(() => {
                        const currentIndex = previewCreatives.findIndex(c => c.id === selectedPreviewCreative.id);
                        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
                        const total = previewCreatives.length;

                        const handlePrev = () => {
                          const prevIdx = (safeIndex - 1 + total) % total;
                          setPreviewCreativeId(previewCreatives[prevIdx].id);
                        };

                        const handleNext = () => {
                          const nextIdx = (safeIndex + 1) % total;
                          setPreviewCreativeId(previewCreatives[nextIdx].id);
                        };

                        const brandDisplayName = briefing?.productOrOffer || campaign.product_or_offer || campaign.name || 'Sua Marca';
                        const brandAvatarLetter = (brandDisplayName.trim().charAt(0) || 'P').toUpperCase();

                        const consciousnessLabels: Record<string, string> = {
                          unaware: 'Inconsciente do Problema',
                          problem_aware: 'Consciente da Dor',
                          solution_aware: 'Buscando Soluções',
                          product_aware: 'Conhece o Produto',
                          most_aware: 'Oferta Direta & Decisão'
                        };

                        const consciousnessColors: Record<string, string> = {
                          unaware: '#64748b',
                          problem_aware: '#ea580c',
                          solution_aware: '#2563eb',
                          product_aware: '#7c3aed',
                          most_aware: '#059669'
                        };

                        const currentLevel = selectedPreviewCreative.consciousnessLevel || 'unaware';
                        const badgeColor = consciousnessColors[currentLevel] || '#6366f1';

                        return (
                          <div>
                            {/* Carrossel Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: isLight ? '#0f172a' : '#fff', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <Eye size={16} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} /> Mockup do Anúncio no Feed (Meta Ads)
                                </span>
                                <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '6px', background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}35`, fontWeight: 700 }}>
                                  Proposta {safeIndex + 1} de {total}: {consciousnessLabels[currentLevel] || currentLevel}
                                </span>
                              </div>

                              {/* Carrossel Quick Nav Controls */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <button
                                  onClick={handlePrev}
                                  title="Anúncio Anterior"
                                  style={{ 
                                    background: isLight ? '#ffffff' : '#1c1c24', 
                                    border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                                    color: isLight ? '#1e293b' : '#fff', 
                                    borderRadius: '8px', 
                                    padding: '0.35rem 0.6rem', 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.2rem', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600,
                                    boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
                                  }}
                                >
                                  <ChevronLeft size={14} /> Anterior
                                </button>
                                <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)', padding: '0 0.25rem' }}>
                                  {safeIndex + 1}/{total}
                                </span>
                                <button
                                  onClick={handleNext}
                                  title="Próximo Anúncio"
                                  style={{ 
                                    background: isLight ? '#ffffff' : '#1c1c24', 
                                    border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                                    color: isLight ? '#1e293b' : '#fff', 
                                    borderRadius: '8px', 
                                    padding: '0.35rem 0.6rem', 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.2rem', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600,
                                    boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
                                  }}
                                >
                                  Próximo <ChevronRight size={14} />
                                </button>
                              </div>
                            </div>

                            {/* Mockup Instagram / Meta Card */}
                            <div style={{ 
                              maxWidth: '420px', 
                              margin: '0 auto', 
                              background: isLight ? '#ffffff' : '#000000', 
                              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.12)', 
                              borderRadius: '14px', 
                              overflow: 'hidden', 
                              boxShadow: isLight ? '0 10px 25px -5px rgba(15,23,42,0.1)' : '0 12px 30px rgba(0,0,0,0.5)' 
                            }}>
                              {/* Header Mockup */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                padding: '0.75rem 1rem', 
                                borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.06)',
                                background: isLight ? '#ffffff' : '#000000'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', fontWeight: 700, overflow: 'hidden' }}>
                                    {briefing?.logoOverlayUrl ? (
                                      <img src={briefing.logoOverlayUrl} alt="Brand Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                      brandAvatarLetter
                                    )}
                                  </div>
                                  <div>
                                    <strong style={{ fontSize: '0.85rem', color: isLight ? '#0f172a' : '#fff', display: 'block' }}>{brandDisplayName}</strong>
                                    <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'rgba(255,255,255,0.6)' }}>Patrocinado</span>
                                  </div>
                                </div>
                                <MoreHorizontal size={18} color={isLight ? '#64748b' : 'rgba(255,255,255,0.6)'} />
                              </div>

                              {/* Image Canvas with Carrossel Side Arrows & Dots */}
                              <div style={{ position: 'relative', width: '100%', minHeight: '280px', background: isLight ? '#f1f5f9' : '#18181b', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {selectedPreviewCreative.assetUrl ? (
                                  <div 
                                    style={{ position: 'relative', width: '100%', cursor: 'zoom-in' }}
                                    onClick={() => {
                                      setZoomedCreative(selectedPreviewCreative);
                                      setSelectedZoomUrl(selectedPreviewCreative.assetUrl || null);
                                    }}
                                    title="Clique para ampliar e ver histórico de versões"
                                  >
                                    <img src={selectedPreviewCreative.assetUrl} alt="Ad Visual" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                    <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', padding: '0.3rem 0.5rem', borderRadius: '6px', color: '#fff', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.3rem', border: '1px solid rgba(255,255,255,0.15)' }}>
                                      <Eye size={12} /> Ampliar
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ padding: '2rem 1.5rem', textAlign: 'center', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                                    <Image size={40} style={{ opacity: 0.3, margin: '0 auto 0.5rem' }} />
                                    <p style={{ fontSize: '0.85rem', margin: 0, color: isLight ? '#1e293b' : '#e2e8f0', fontWeight: 600 }}>Arte conceitual pendente de síntese</p>
                                    <p style={{ fontSize: '0.75rem', margin: '0.35rem 0 0 0', color: isLight ? '#64748b' : 'var(--text-muted)' }}>Clique em "Sintetizar Todas as Artes" para gravar no Cloudflare R2</p>
                                    
                                    {(() => {
                                      const isImageIncluded = briefing?.includeImage !== undefined ? briefing.includeImage : (briefing?.visualOverlayMode === 'image_in_image');
                                      const isTextIncluded = briefing?.includeText !== undefined ? briefing.includeText : (briefing?.visualOverlayMode !== 'pure_image');
                                      const textToDisplay = briefing?.customOverlayText || selectedPreviewCreative.overlayText || selectedPreviewCreative.headline;

                                      if (!isImageIncluded && !isTextIncluded) {
                                        return (
                                          <div style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', background: isLight ? '#ecfdf5' : 'rgba(160,185,129,0.15)', border: `1px solid ${isLight ? '#6ee7b7' : '#10b981'}`, borderRadius: '6px', color: isLight ? '#047857' : '#6ee7b7', fontWeight: 700, fontSize: '0.78rem' }}>
                                            <span>✨</span> Fotografia Pura (Sem Texto/Logo)
                                          </div>
                                        );
                                      }

                                      return (
                                        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center' }}>
                                          {isImageIncluded && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', background: isLight ? '#faf5ff' : 'rgba(168,85,247,0.15)', border: `1px solid ${isLight ? '#c084fc' : '#a855f7'}`, borderRadius: '6px', color: isLight ? '#7e22ce' : '#d8b4fe', fontWeight: 700, fontSize: '0.75rem' }}>
                                              <span>🖼️</span> Imagem Integrada
                                            </span>
                                          )}
                                          {isTextIncluded && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', background: isLight ? '#fef3c7' : '#000', border: `1px solid ${isLight ? '#f59e0b' : '#fbbf24'}`, borderRadius: '6px', color: isLight ? '#92400e' : '#fbbf24', fontWeight: 700, fontSize: '0.75rem' }}>
                                              <span>🔤</span> "{textToDisplay}"
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* Badge Overlay de Texto quando a imagem estiver carregada */}
                                {selectedPreviewCreative.assetUrl && (briefing?.includeText !== false && briefing?.visualOverlayMode !== 'pure_image') && (
                                  <div style={{ position: 'absolute', bottom: '28px', left: '12px', right: '12px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', padding: '0.4rem 0.8rem', borderRadius: '6px', borderLeft: '3px solid #fbbf24', color: '#fff', fontWeight: 700, fontSize: '0.85rem', pointerEvents: 'none' }}>
                                    {briefing?.customOverlayText || selectedPreviewCreative.overlayText || selectedPreviewCreative.headline}
                                  </div>
                                )}

                                {/* Floating Carrossel Arrows */}
                                <button
                                  onClick={handlePrev}
                                  aria-label="Variação anterior"
                                  style={{
                                    position: 'absolute',
                                    left: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'rgba(0,0,0,0.65)',
                                    color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 5
                                  }}
                                >
                                  <ChevronLeft size={16} />
                                </button>
                                <button
                                  onClick={handleNext}
                                  aria-label="Próxima variação"
                                  style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'rgba(0,0,0,0.65)',
                                    color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 5
                                  }}
                                >
                                  <ChevronRight size={16} />
                                </button>

                                {/* Carrossel Pagination Dots (Instagram style) */}
                                <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '4px', zIndex: 4, background: 'rgba(0,0,0,0.5)', padding: '2px 6px', borderRadius: '10px' }}>
                                  {previewCreatives.map((c, i) => (
                                    <button
                                      key={c.id}
                                      onClick={() => setPreviewCreativeId(c.id)}
                                      title={`Ir para Proposta ${i + 1}`}
                                      style={{
                                        width: i === safeIndex ? '18px' : '6px',
                                        height: '6px',
                                        borderRadius: '3px',
                                        background: i === safeIndex ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>

                              {/* Action Bar */}
                              <div style={{ 
                                background: isLight ? '#f8fafc' : '#1c1917', 
                                padding: '0.6rem 1rem', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                borderTop: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.05)' 
                              }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '0.5rem' }}>
                                  <strong style={{ fontSize: '0.8rem', color: isLight ? '#0f172a' : '#fff', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedPreviewCreative.headline}</strong>
                                  <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'rgba(255,255,255,0.6)' }}>{campaign.landing_url || 'seusite.com.br/oferta'}</span>
                                </div>
                                <button style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {selectedPreviewCreative.callToActionType.replace(/_/g, ' ')}
                                </button>
                              </div>

                              {/* Caption */}
                              <div style={{ 
                                padding: '0.75rem 1rem', 
                                fontSize: '0.8rem', 
                                color: isLight ? '#1e293b' : 'rgba(255,255,255,0.85)', 
                                lineHeight: 1.4, 
                                borderTop: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.05)' 
                              }}>
                                <strong style={{ color: isLight ? '#0f172a' : '#fff', marginRight: '0.35rem' }}>{brandDisplayName}</strong>
                                {selectedPreviewCreative.primaryText}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      </div>
                    </div>
                  )}

                  {/* Lista de 5 Ângulos com editor inline e ações */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.05rem', color: isLight ? '#0f172a' : 'var(--text-color)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700 }}>
                        <Brain size={18} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} /> Matriz Narrativa (5 Níveis de Consciência)
                      </h3>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ 
                          fontSize: '0.78rem', 
                          fontWeight: 700, 
                          padding: '0.35rem 0.65rem', 
                          borderRadius: '8px', 
                          background: creatives.filter(c => !!c.assetUrl).length === creatives.length ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                          color: creatives.filter(c => !!c.assetUrl).length === creatives.length ? '#10b981' : '#f59e0b',
                          border: creatives.filter(c => !!c.assetUrl).length === creatives.length ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(245,158,11,0.3)'
                        }}>
                          🎨 {creatives.filter(c => !!c.assetUrl).length}/{creatives.length} artes prontas
                        </span>
                        <button 
                          onClick={handleSynthesizeAll}
                          disabled={isSynthesizingAll || creatives.length === 0}
                          className="primary-button"
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', display: 'flex', gap: '0.3rem', alignItems: 'center' }}
                        >
                          {isSynthesizingAll ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                          {isSynthesizingAll ? 'Sintetizando...' : 'Sintetizar Todas as Artes (Lote)'}
                        </button>
                        <button 
                          onClick={() => upgradeMCAMutation.mutate({})}
                          disabled={upgradeMCAMutation.isPending || isSynthesizingAll}
                          className="secondary-button"
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '0.35rem 0.75rem',
                            background: isLight ? '#ffffff' : 'transparent',
                            border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)',
                            color: isLight ? '#334155' : 'inherit'
                          }}
                        >
                          <RefreshCw size={13} className={upgradeMCAMutation.isPending ? 'spin' : ''} /> Regerar Matriz
                        </button>
                      </div>
                    </div>

                    {isSynthesizingAll && (
                      <div style={{ 
                        background: isLight ? '#eef2ff' : 'rgba(99, 102, 241, 0.1)', 
                        border: isLight ? '1px solid #c7d2fe' : '1px solid rgba(99, 102, 241, 0.3)', 
                        padding: '0.75rem 1rem', 
                        borderRadius: '8px', 
                        color: isLight ? '#3730a3' : '#a5b4fc', 
                        fontSize: '0.85rem', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.6rem' 
                      }}>
                        <Loader2 size={16} className="spin" />
                        <span>{synthProgress}</span>
                      </div>
                    )}

                    {reviewCreatives.map((cr, idx) => {
                      const isEditing = editingCreative?.id === cr.id;
                      const isSelected = selectedPreviewCreative?.id === cr.id;

                      const consciousnessColors: Record<string, string> = {
                        unaware: '#64748b',
                        problem_aware: '#ea580c',
                        solution_aware: '#2563eb',
                        product_aware: '#7c3aed',
                        most_aware: '#059669'
                      };

                      const color = consciousnessColors[cr.consciousnessLevel || 'unaware'] || '#6366f1';

                      return (
                        <div 
                          key={cr.id} 
                          style={{ 
                            background: isLight 
                              ? (isSelected ? '#f8fafc' : '#ffffff') 
                              : (isSelected ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)'), 
                            border: isLight 
                              ? `1px solid ${isSelected ? '#6366f1' : '#e2e8f0'}` 
                              : `1px solid ${isSelected ? 'var(--primary-color)' : 'var(--border-color)'}`, 
                            boxShadow: isLight ? (isSelected ? '0 4px 14px rgba(99,102,241,0.1)' : '0 1px 3px rgba(0,0,0,0.04)') : 'none',
                            padding: '1.25rem', 
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            position: 'relative'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: color, color: '#fff', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {cr.stepOrder || creatives.findIndex(item => item.id === cr.id) + 1}
                              </span>
                              <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem' }}>{cr.angleName || cr.name}</strong>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '6px', background: `${color}15`, color: color, border: `1px solid ${color}35` }}>
                                {cr.consciousnessLevel?.replace('_', ' ')}
                              </span>
                              <button 
                                onClick={() => setPreviewCreativeId(cr.id)}
                                style={{ background: 'none', border: 'none', color: isSelected ? (isLight ? '#4f46e5' : 'var(--primary-color)') : (isLight ? '#64748b' : 'var(--text-muted)'), cursor: 'pointer', padding: '0.25rem' }}
                                title="Ver no Feed Mockup"
                              >
                                <Eye size={16} />
                              </button>
                            </div>
                          </div>

                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', background: isLight ? '#f1f5f9' : '#141418', padding: '1rem', borderRadius: '8px', border: isLight ? '1px solid #e2e8f0' : 'none' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                  <label style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Título do Ângulo / Fase</label>
                                  <input 
                                    type="text" 
                                    value={editingCreative.angleName || editingCreative.name || ''}
                                    onChange={(e) => setEditingCreative({ ...editingCreative, angleName: e.target.value, name: e.target.value })}
                                    placeholder="Ex: Topo de Funil - Consciência"
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#1c1c22', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Headline Principal (Título do Anúncio)</label>
                                  <input 
                                    type="text" 
                                    value={editingCreative.headline}
                                    onChange={(e) => setEditingCreative({ ...editingCreative, headline: e.target.value })}
                                    placeholder="Headline do anúncio"
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#1c1c22', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                  />
                                </div>
                              </div>

                              <div>
                                <label style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Texto Principal (Copy do Feed)</label>
                                <textarea 
                                  rows={3}
                                  value={editingCreative.primaryText}
                                  onChange={(e) => setEditingCreative({ ...editingCreative, primaryText: e.target.value })}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#1c1c22', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                />
                              </div>

                              <div>
                                <label style={{ fontSize: '0.75rem', color: isLight ? '#0284c7' : '#38bdf8', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                                  💡 Metáfora Visual (Conceito da Imagem para a IA)
                                </label>
                                <textarea 
                                  rows={2}
                                  value={editingCreative.visualMetaphor || ''}
                                  onChange={(e) => setEditingCreative({ ...editingCreative, visualMetaphor: e.target.value })}
                                  placeholder="Descreva a metáfora visual e elementos visuais da cena..."
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #7dd3fc' : '1px solid rgba(56, 189, 248, 0.4)', background: isLight ? '#f0f9ff' : 'rgba(56, 189, 248, 0.05)', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                />
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                  <label style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Texto Overlay na Arte</label>
                                  <input 
                                    type="text" 
                                    value={editingCreative.overlayText || ''}
                                    onChange={(e) => setEditingCreative({ ...editingCreative, overlayText: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#1c1c22', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>Botão CTA</label>
                                  <select 
                                    value={editingCreative.callToActionType}
                                    onChange={(e) => setEditingCreative({ ...editingCreative, callToActionType: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#1c1c22', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                                  >
                                    <option value="SAIBA_MAIS">SAIBA MAIS</option>
                                    <option value="FALAR_COM_ESPECIALISTA">FALAR COM ESPECIALISTA</option>
                                    <option value="ENVIAR_MENSAGEM">ENVIAR MENSAGEM</option>
                                    <option value="OBTER_OFERTA">OBTER OFERTA</option>
                                  </select>
                                </div>
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <button 
                                  onClick={() => setEditingCreative(null)}
                                  className="secondary-button"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: isLight ? '#ffffff' : 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', color: isLight ? '#334155' : 'inherit' }}
                                >
                                  Cancelar
                                </button>
                                <button 
                                  onClick={() => updateCreativeMutation.mutate({ creativeId: cr.id, data: editingCreative })}
                                  disabled={updateCreativeMutation.isPending}
                                  className="primary-button"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', display: 'flex', gap: '0.3rem', alignItems: 'center' }}
                                >
                                  <Save size={13} /> Salvar Copy
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ 
                              background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.25)', 
                              border: isLight ? '1px solid #e2e8f0' : 'none',
                              padding: '0.9rem', 
                              borderRadius: '8px', 
                              borderLeft: `3px solid ${color}` 
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', margin: '0 0 0.4rem 0', fontWeight: 700 }}>{cr.headline}</h4>
                                <button 
                                  onClick={() => setEditingCreative(cr)}
                                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.2rem' }}
                                  title="Editar Copy"
                                >
                                  <Edit3 size={14} />
                                </button>
                              </div>
                              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.4, margin: 0 }}>
                                {cr.primaryText}
                              </p>

                              {cr.visualMetaphor && (
                                <div style={{ marginTop: '0.6rem', padding: '0.4rem 0.6rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', fontSize: '0.75rem', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.05)' }}>
                                  <strong>Metáfora:</strong> {cr.visualMetaphor}
                                </div>
                              )}

                              {/* Preview do Roteiro de Vídeo (Cinema Studio AI) */}
                              {(cr as any).videoScript && (
                                <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.8rem', background: isLight ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.1)', borderRadius: '8px', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isLight ? '#4f46e5' : '#a5b4fc', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                      🎙️ Roteiro de Narração Publicitária (HeyGen Quality):
                                    </span>
                                    {(cr as any).videoNarrationUrl && (
                                      <span style={{ fontSize: '0.65rem', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 700 }}>
                                        🔊 ÁUDIO PRONTO
                                      </span>
                                    )}
                                  </div>
                                  <p style={{ color: isLight ? '#334155' : '#e2e8f0', fontSize: '0.8rem', fontStyle: 'italic', margin: 0, lineHeight: 1.4 }}>
                                    "{(cr as any).videoScript}"
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Seção da Imagem / R2 & Histórico de Versões */}
                          <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.9rem', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {cr.videoUrl ? (
                                  <div 
                                    style={{ position: 'relative', width: '52px', height: '52px', cursor: 'pointer', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', border: '2px solid #6366f1', background: '#000' }}
                                    onClick={() => {
                                      setZoomedCreative(cr);
                                      setSelectedZoomUrl(cr.videoUrl || null);
                                      setZoomMediaTab('video');
                                    }}
                                    title="Clique para assistir ao vídeo"
                                  >
                                    <video src={cr.videoUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Play size={16} color="#fff" fill="#fff" />
                                    </div>
                                  </div>
                                ) : cr.assetUrl ? (
                                  <div 
                                    style={{ position: 'relative', width: '52px', height: '52px', cursor: 'zoom-in', flexShrink: 0 }}
                                    onClick={() => {
                                      setZoomedCreative(cr);
                                      setSelectedZoomUrl(cr.assetUrl || null);
                                    }}
                                    title="Clique para ampliar arte e ver histórico"
                                  >
                                    <img src={cr.assetUrl} alt="Creative" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '2px solid #10b981' }} />
                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', borderRadius: '8px', opacity: 0, transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')} onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}>
                                      <Eye size={16} color="#fff" />
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ width: '52px', height: '52px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Image size={24} style={{ color: 'var(--text-muted)' }} />
                                  </div>
                                )}
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#fff' }}>
                                      {cr.videoUrl ? 'Vídeo Ativo do Anúncio' : cr.assetUrl ? 'Arte Ativa do Anúncio' : 'Sem mídia anexada'}
                                    </span>
                                    {(cr.videoUrl || cr.assetUrl) && (
                                      <span style={{ fontSize: '0.65rem', background: cr.videoUrl ? 'rgba(99, 102, 241, 0.15)' : 'rgba(16, 185, 129, 0.15)', color: cr.videoUrl ? '#818cf8' : '#34d399', border: `1px solid ${cr.videoUrl ? 'rgba(99, 102, 241, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`, padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 700 }}>
                                        {cr.videoUrl ? 'VÍDEO ATIVO' : 'EM USO'}
                                      </span>
                                    )}
                                  </div>
                                  {cr.overlayText && (
                                    <span style={{ fontSize: '0.75rem', color: '#fbbf24', display: 'block', marginTop: '0.15rem' }}>
                                      Overlay: "{cr.overlayText}"
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', justifyContent: 'flex-start' }}>
                                {/* Grupo A: Formatos de Arte Visual */}
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.35rem', 
                                  background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)', 
                                  padding: '0.3rem 0.5rem', 
                                  borderRadius: '10px', 
                                  border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.07)' 
                                }}>
                                  <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : '#94a3b8', fontWeight: 700, paddingRight: '0.2rem' }}>ARTE:</span>
                                  
                                  <label className="secondary-button" style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    {uploadImageMutation.isPending && uploadImageMutation.variables?.creativeId === cr.id ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                                    Upload
                                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileUpload(cr.id, e)} disabled={uploadImageMutation.isPending} />
                                  </label>

                                  <button 
                                    type="button"
                                    onClick={() => generateImageMutation.mutate({ creativeId: cr.id, format: 'feed' })}
                                    disabled={generateImageMutation.isPending && (generateImageMutation.variables === cr.id || (typeof generateImageMutation.variables === 'object' && generateImageMutation.variables?.creativeId === cr.id))}
                                    className="secondary-button"
                                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem', display: 'flex', gap: '0.25rem', alignItems: 'center' }}
                                    title="Sintetizar arte no formato quadrado para Feed / Carrossel (1080x1080)"
                                  >
                                    {generateImageMutation.isPending && (generateImageMutation.variables === cr.id || (typeof generateImageMutation.variables === 'object' && generateImageMutation.variables?.creativeId === cr.id && (generateImageMutation.variables as any)?.format === 'feed')) ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                                    🔲 Feed (1:1)
                                  </button>

                                  <button 
                                    type="button"
                                    onClick={() => generateImageMutation.mutate({ creativeId: cr.id, format: 'stories' })}
                                    disabled={generateImageMutation.isPending && (generateImageMutation.variables === cr.id || (typeof generateImageMutation.variables === 'object' && generateImageMutation.variables?.creativeId === cr.id))}
                                    className="secondary-button"
                                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem', display: 'flex', gap: '0.25rem', alignItems: 'center', borderColor: isLight ? '#f472b6' : '#ec4899', color: isLight ? '#db2777' : '#f472b6' }}
                                    title="Sintetizar arte compatível no formato vertical para Stories / Reels (1080x1920)"
                                  >
                                    {generateImageMutation.isPending && (typeof generateImageMutation.variables === 'object' && generateImageMutation.variables?.creativeId === cr.id && (generateImageMutation.variables as any)?.format === 'stories') ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                                    📱 Stories (9:16)
                                  </button>
                                </div>

                                {/* Grupo B: Marca & Logo (quando houver imagem gerada) */}
                                {cr.assetUrl && (
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.35rem', 
                                    background: isLight ? '#f5f3ff' : 'rgba(139,92,246,0.06)', 
                                    padding: '0.3rem 0.5rem', 
                                    borderRadius: '10px', 
                                    border: isLight ? '1px solid #ddd6fe' : '1px solid rgba(139,92,246,0.2)' 
                                  }}>
                                    <span style={{ fontSize: '0.7rem', color: isLight ? '#7c3aed' : '#a78bfa', fontWeight: 700, paddingRight: '0.2rem' }}>MARCA:</span>
                                    <button
                                      type="button"
                                      onClick={() => setLogoPositioningCreative(cr)}
                                      className="secondary-button"
                                      style={{ 
                                        padding: '0.35rem 0.6rem', 
                                        fontSize: '0.75rem', 
                                        display: 'flex', 
                                        gap: '0.25rem', 
                                        alignItems: 'center', 
                                        borderColor: '#8b5cf6', 
                                        color: isLight ? '#7c3aed' : '#a78bfa',
                                        fontWeight: 600
                                      }}
                                      title="Abrir Canvas para posicionar e redimensionar a logo manualmente sobre o criativo"
                                    >
                                      <Move size={13} />
                                      🏷️ Posicionar Logo
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setZoomedCreative(cr);
                                        setSelectedZoomUrl(cr.assetUrl || null);
                                        setZoomMediaTab(cr.videoUrl && !cr.assetUrl ? 'video' : 'image');
                                      }}
                                      className="secondary-button"
                                      style={{ padding: '0.35rem 0.55rem', fontSize: '0.75rem', display: 'flex', gap: '0.2rem', alignItems: 'center' }}
                                      title="Ver arte ampliada, galeria de versões e histórico"
                                    >
                                      <Maximize2 size={13} />
                                    </button>
                                  </div>
                                )}

                                {/* Ação Dedicada: Estúdio de Vídeo Audiovisual */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.4rem',
                                  background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.08)',
                                  padding: '0.35rem 0.65rem',
                                  borderRadius: '10px',
                                  border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(59,130,246,0.25)'
                                }}>
                                  {cr.videoUrl ? (
                                    <span style={{ fontSize: '0.74rem', color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                      <CheckCircle2 size={13} /> Vídeo Pronto
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '0.72rem', color: isLight ? '#2563eb' : '#93c5fd', fontWeight: 700 }}>
                                      VÍDEO IA:
                                    </span>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedStudioCreativeId(cr.id);
                                      setActiveTab('video-studio');
                                    }}
                                    className="secondary-button"
                                    style={{
                                      padding: '0.35rem 0.75rem',
                                      fontSize: '0.75rem',
                                      display: 'flex',
                                      gap: '0.35rem',
                                      alignItems: 'center',
                                      borderColor: '#3b82f6',
                                      color: isLight ? '#1d4ed8' : '#93c5fd',
                                      fontWeight: 700,
                                      background: isLight ? '#ffffff' : 'rgba(59, 130, 246, 0.15)',
                                      borderRadius: '6px'
                                    }}
                                    title="Abrir este roteiro no Estúdio de Vídeo dedicado da campanha"
                                  >
                                    <Film size={13} style={{ color: '#3b82f6' }} />
                                    🎬 Produzir no Estúdio de Vídeo
                                  </button>
                                </div>

                                {cr.videoStatus === 'failed' && cr.videoError && (
                                  <span style={{ color: '#ef4444', fontSize: '0.72rem' }} title={cr.videoError}>
                                    Filme IA falhou: {cr.videoError}
                                  </span>
                                )}


                                {cr.assetUrl && (
                                  <button
                                    onClick={() => {
                                      if (confirm('Deseja realmente apagar esta imagem do Cloudflare R2?')) {
                                        deleteImageMutation.mutate({ creativeId: cr.id, assetUrl: cr.assetUrl });
                                      }
                                    }}
                                    disabled={deleteImageMutation.isPending}
                                    className="secondary-button"
                                    style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                    title="Apagar imagem ativa do R2"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* SELETOR / GALERIA DE VERSÕES GERADAS */}
                            {cr.imageHistory && cr.imageHistory.length > 0 && (
                              <div style={{ borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', paddingTop: '0.6rem', marginTop: '0.4rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                  <span style={{ fontSize: '0.75rem', color: isLight ? '#475569' : '#94a3b8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <span>🔄</span> Galeria de Versões ({cr.imageHistory.length}):
                                  </span>
                                  <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : '#64748b' }}>Clique para ativar ou no 🗑️ para excluir do R2</span>
                                </div>

                                <div style={{ display: 'flex', gap: '0.6rem', overflowX: 'auto', paddingBottom: '0.35rem' }}>
                                  {cr.imageHistory.map((hist, hIdx) => {
                                    const isActive = hist.url === cr.assetUrl;
                                    const isRestoring = restoreImageMutation.isPending && restoreImageMutation.variables?.assetUrl === hist.url;
                                    const isDeleting = deleteImageMutation.isPending && deleteImageMutation.variables?.assetUrl === hist.url;

                                    return (
                                      <div
                                        key={hist.id || hIdx}
                                        style={{
                                          position: 'relative',
                                          width: '60px',
                                          height: '60px',
                                          borderRadius: '8px',
                                          overflow: 'hidden',
                                          border: isActive ? '2px solid #10b981' : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                                          opacity: isActive ? 1 : 0.85,
                                          transition: 'all 0.2s ease',
                                          flexShrink: 0,
                                          background: '#000',
                                          boxShadow: isActive ? '0 0 8px rgba(16,185,129,0.3)' : 'none'
                                        }}
                                        className="group"
                                      >
                                        <img 
                                          src={hist.url} 
                                          alt={`Versão ${hIdx + 1}`} 
                                          onClick={() => !isActive && !isRestoring && !isDeleting && restoreImageMutation.mutate({ creativeId: cr.id, assetUrl: hist.url })}
                                          title={isActive ? 'Versão ativa em uso' : 'Clique para ativar esta versão'}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: isActive ? 'default' : 'pointer' }} 
                                        />
                                        
                                        {/* Botão de excluir versão específica do R2 */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (confirm(`Deseja apagar permanentemente a versão ${hIdx + 1} do Cloudflare R2?`)) {
                                              deleteImageMutation.mutate({ creativeId: cr.id, assetUrl: hist.url });
                                            }
                                          }}
                                          title="Excluir permanentemente esta versão do R2"
                                          style={{
                                            position: 'absolute',
                                            top: '2px',
                                            right: '2px',
                                            background: 'rgba(239, 68, 68, 0.92)',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10,
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.6)'
                                          }}
                                        >
                                          <Trash2 size={11} />
                                        </button>

                                        {isActive ? (
                                          <div style={{ position: 'absolute', bottom: 0, insetInline: 0, background: 'rgba(16, 185, 129, 0.95)', color: '#fff', fontSize: '8px', textAlign: 'center', fontWeight: 800, padding: '1px 0', letterSpacing: '0.5px' }}>
                                            ATIVA
                                          </div>
                                        ) : isRestoring || isDeleting ? (
                                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Loader2 size={14} className="spin text-white" />
                                          </div>
                                        ) : (
                                          <div 
                                            onClick={() => restoreImageMutation.mutate({ creativeId: cr.id, assetUrl: hist.url })}
                                            style={{ position: 'absolute', bottom: 0, insetInline: 0, background: 'rgba(0,0,0,0.75)', color: '#cbd5e1', fontSize: '8px', textAlign: 'center', padding: '1px 0', cursor: 'pointer', fontWeight: 600 }}
                                          >
                                            USAR
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            </CampaignReviewTabs>
          )}

          {activeTab === 'video-studio' && (
            <CampaignVideoStudioTab
              campaign={campaign}
              creatives={creatives}
              selectedCreativeId={selectedStudioCreativeId}
              onSelectCreativeId={setSelectedStudioCreativeId}
              isLight={isLight}
              onNavigateToMatrix={() => setActiveTab('strategy')}
            />
          )}

          {activeTab === 'briefing' && (
            briefingQuery.isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', gap: '1rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                <Loader2 size={32} className="spin" style={{ color: 'var(--primary-color)' }} />
                <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Carregando dados do briefing gravado...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <BriefingEditor 
                  campaign={campaign} 
                  briefing={briefing} 
                  onUpgradeMCA={(data) => upgradeMCAMutation.mutate(data)} 
                  isUpgrading={upgradeMCAMutation.isPending} 
                  init={init} 
                  onAdvance={() => setActiveTab('strategy')}
                />
              </div>
            )
          )}

          {activeTab === 'instagram' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <CampaignPublicationJourney campaign={googleProposals.campaign} creatives={creatives} status={currentStatus}
                approving={transitionMutation.isPending} metaPending={syncMetaAdsMutation.isPending}
                googlePending={syncGoogleAdsMutation.isPending}
                onApprove={() => transitionMutation.mutate('pronta_publicacao')}
                onPublishMeta={() => syncMetaAdsMutation.mutate({ recreateAds: false })}
                onPublishGoogle={() => syncGoogleAdsMutation.mutate({ autoActivate: false })}
                onConfigure={() => setActiveTab('briefing')} onReview={() => setActiveTab('strategy')} />

              {/* Header do Instagram Orgânico */}
              {channels.includes('meta') && <><div style={{
                background: isLight ? 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(168,85,247,0.08))' : 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(168,85,247,0.1))', 
                border: isLight ? '1px solid rgba(236,72,153,0.3)' : '1px solid rgba(236,72,153,0.25)', 
                borderRadius: '16px', 
                padding: '1.5rem' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ color: isLight ? '#831843' : '#fff', fontSize: '1.15rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.6rem', margin: 0 }}>
                      <Instagram size={20} style={{ color: '#ec4899' }} /> Instagram orgânico · Publicação opcional
                    </h3>
                    <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.35rem', lineHeight: '1.4' }}>
                      Publique as artes aprovadas da esteira MCA para seus seguidores no Feed e Stories. Os posts orgânicos acumulam curtidas e comentários que fortalecem a presença da marca e as campanhas de aquisição.
                    </p>
                  </div>
                </div>

                {/* Slots Oficiais de Horário */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none' }}>
                    <Clock size={16} style={{ color: '#ec4899' }} />
                    <div>
                      <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Manhã (Pico 1)</span>
                      <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>07:00</strong>
                    </div>
                  </div>

                  <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none' }}>
                    <Clock size={16} style={{ color: '#a855f7' }} />
                    <div>
                      <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Almoço (Pico 2)</span>
                      <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>13:00</strong>
                    </div>
                  </div>

                  <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none' }}>
                    <Clock size={16} style={{ color: '#0284c7' }} />
                    <div>
                      <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Noite (Pico 3)</span>
                      <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>19:00</strong>
                    </div>
                  </div>
                </div>

                {/* Opção Gatilho 'QUERO' */}
                <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <input
                    type="checkbox"
                    id="triggerQuero"
                    checked={addCommentTrigger}
                    onChange={(e) => setAddCommentTrigger(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#ec4899', cursor: 'pointer' }}
                  />
                  <label htmlFor="triggerQuero" style={{ fontSize: '0.85rem', color: isLight ? '#0f172a' : '#fff', cursor: 'pointer', fontWeight: 600 }}>
                    Adicionar chamada para automação na legenda: <span style={{ color: isLight ? '#db2777' : '#f472b6', fontWeight: 700 }}>Comente "QUERO" para receber no Direct</span>
                  </label>
                </div>
              </div>

              {/* Botões de Ação de Publicação Orgânica */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                {/* 1. Agendar Sequência 07:00, 13:00, 19:00 */}
                <div className="page-card" style={{ padding: '1.25rem', background: isLight ? '#ffffff' : '#18181f', border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', borderRadius: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                  <div>
                    <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      📅 Sequência de 5 Dias
                    </h4>
                    <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4' }}>
                      Agenda automaticamente 1 post por slot nos horários de 07:00, 13:00 e 19:00 acompanhando a progressão dos 5 níveis de consciência.
                    </p>
                  </div>
                  <button
                    onClick={() => scheduleInstagramMutation.mutate()}
                    disabled={scheduleInstagramMutation.isPending || creatives.length === 0}
                    className="primary-button"
                    style={{ marginTop: '1rem', background: 'linear-gradient(135deg, #ec4899, #d946ef)', border: 'none', padding: '0.65rem 1rem', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', color: '#fff' }}
                  >
                    {scheduleInstagramMutation.isPending ? <Loader2 size={15} className="spin" /> : <Calendar size={15} />}
                    Agendar Posts (07h, 13h, 19h)
                  </button>
                </div>

                {/* 2. Publicar Carrossel Completo */}
                <div className="page-card" style={{ padding: '1.25rem', background: isLight ? '#ffffff' : '#18181f', border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', borderRadius: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                  <div>
                    <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      📸 Carrossel Completo (5 Slides)
                    </h4>
                    <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4' }}>
                      Publica instantaneamente as 5 artes em um único post Carrossel no feed do Instagram. Máximo alcance e retenção.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      // Monta a sugestão fluida inicial com os dados atuais da campanha
                      const sortedCreatives = [...creatives].sort((a, b) => (a.stepOrder || 1) - (b.stepOrder || 1));
                      const step1 = sortedCreatives.find(c => c.stepOrder === 1) || sortedCreatives[0];
                      const step2 = sortedCreatives.find(c => c.stepOrder === 2);
                      const step3 = sortedCreatives.find(c => c.stepOrder === 3);
                      const step4 = sortedCreatives.find(c => c.stepOrder === 4);
                      const step5 = sortedCreatives.find(c => c.stepOrder === 5) || sortedCreatives[sortedCreatives.length - 1];

                      const productName = (briefing?.productOrOffer || (briefing as any)?.product_or_offer || campaign?.name || 'nossa solução').trim();
                      const lines: string[] = [];

                      if (step1?.headline) {
                        lines.push(`🚀 ${step1.headline.trim()}`);
                      }
                      if (step1?.primaryText) {
                        lines.push(step1.primaryText.trim());
                      }

                      const step2Text = (step2?.primaryText || step2?.headline || '').trim();
                      if (step2Text && !lines.some(l => l.includes(step2Text))) {
                        lines.push(`A verdade é que o maior gargalo está na raiz do processo: ${step2Text}`);
                      }

                      const step3Text = (step3?.primaryText || step3?.headline || '').trim();
                      if (step3Text && !lines.some(l => l.includes(step3Text))) {
                        lines.push(`É exatamente por isso que métodos tradicionais não funcionam mais. A virada de chave acontece quando você adota uma abordagem moderna: ${step3Text}`);
                      }

                      const step4Text = (step4?.primaryText || step4?.headline || '').trim();
                      if (step4Text && !lines.some(l => l.includes(step4Text))) {
                        if (step4Text.toLowerCase().includes(productName.toLowerCase())) {
                          lines.push(step4Text);
                        } else {
                          lines.push(`Com o ${productName}, você tem em mãos a estrutura completa para acelerar seus resultados. ${step4Text}`);
                        }
                      }

                      const step5Text = (step5?.primaryText || step5?.headline || '').trim();
                      if (step5Text && !lines.some(l => l.includes(step5Text))) {
                        lines.push(`✨ ${step5Text}`);
                      }

                      if (addCommentTrigger) {
                        lines.push(`👇 Quer ter acesso completo ao ${productName} com bônus e condições exclusivas?\n\n💬 Comente "QUERO" aqui embaixo que nossa equipe envia o link direto com todos os detalhes no seu Direct!`);
                      }

                      const cleanTag = (str: string) => (str || '').replace(/[^a-zA-Z0-9]/g, '');
                      const nicheTag = cleanTag((briefing as any)?.marketNiche || (briefing as any)?.market_niche || '');
                      const tags = [cleanTag(productName), nicheTag, 'Estrategia', 'Metodo', 'Resultados', 'Crescimento', 'AltaPerformance']
                        .filter(Boolean)
                        .filter((v, idx, arr) => arr.indexOf(v) === idx)
                        .map(t => `#${t}`);

                      if (tags.length > 0) {
                        lines.push(tags.join(' '));
                      }

                      setCarouselCustomCaption(lines.join('\n\n'));
                      setIsCarouselModalOpen(true);
                    }}
                    disabled={publishCarouselMutation.isPending || creatives.length === 0}
                    className="secondary-button"
                    style={{ marginTop: '1rem', borderColor: isLight ? '#c084fc' : '#a855f7', color: isLight ? '#7e22ce' : '#c084fc', background: isLight ? '#fdf4ff' : 'transparent', padding: '0.65rem 1rem', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                  >
                    <Layers size={15} />
                    Revisar & Publicar Carrossel
                  </button>
                </div>

                {/* 3. Publicar Stories com Seleção de Steps e Imagens */}
                <div className="page-card" style={{ padding: '1.25rem', background: isLight ? '#ffffff' : '#18181f', border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', borderRadius: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                  <div>
                    <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      ⚡ Publicar nos Stories
                    </h4>
                    <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.8rem', lineHeight: '1.4' }}>
                      Escolha qualquer Step (1 a 5) da esteira, selecione a imagem ideal (Feed ou Stories 9:16) e publique direto no Instagram.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const target = creatives.find(c => c.stepOrder === 5) || creatives[0];
                      const step = target ? (target.stepOrder || 1) : 5;
                      setStorySelectedStep(step);
                      setStorySelectedImageUrl(target?.assetUrl || null);
                      setStoryCustomCaption(target ? `🔥 ${target.headline}\n\n${target.primaryText || ''}\n\n💬 Responda ou Comente "QUERO" para receber a proposta no seu Direct!` : '');
                      setIsStoryModalOpen(true);
                    }}
                    disabled={creatives.length === 0}
                    className="secondary-button"
                    style={{ marginTop: '1rem', borderColor: isLight ? '#86efac' : '#10b981', color: isLight ? '#047857' : '#34d399', background: isLight ? '#f0fdf4' : 'transparent', padding: '0.65rem 1rem', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                  >
                    <Zap size={15} />
                    Escolher Step & Publicar Story
                  </button>
                </div>
              </div>

              {/* Lista de Posts Agendados / Publicados */}
              <div className="page-card" style={{ padding: '1.5rem', background: isLight ? '#ffffff' : '#141418', border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', borderRadius: '16px', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Activity size={18} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} /> Grade de Publicações no Instagram
                  </h4>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {(instagramPostsQuery.data?.posts || []).some(p => p.status === 'scheduled') && (
                      <button
                        onClick={() => {
                          if (confirm('Deseja limpar todos os agendamentos pendentes desta campanha?')) {
                            clearScheduleMutation.mutate();
                          }
                        }}
                        disabled={clearScheduleMutation.isPending}
                        className="secondary-button"
                        style={{ borderColor: isLight ? '#fca5a5' : '#ef4444', color: isLight ? '#b91c1c' : '#f87171', background: isLight ? '#fef2f2' : 'transparent', padding: '0.4rem 0.8rem', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        {clearScheduleMutation.isPending ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
                        Limpar Agendamentos Pendentes
                      </button>
                    )}
                    <button
                      onClick={() => instagramPostsQuery.refetch()}
                      disabled={instagramPostsQuery.isFetching}
                      className="secondary-button"
                      style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <RefreshCw size={13} className={instagramPostsQuery.isFetching ? 'spin' : ''} />
                      Atualizar
                    </button>
                  </div>
                </div>

                {instagramPostsQuery.isError ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: '#ef4444', background: isLight ? '#fef2f2' : 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>
                      ⚠️ Erro ao carregar grade: {(instagramPostsQuery.error as any)?.message || 'Erro de conexão'}
                    </p>
                    <button
                      onClick={() => instagramPostsQuery.refetch()}
                      className="secondary-button"
                      style={{ marginTop: '0.75rem', padding: '0.35rem 0.8rem', fontSize: '0.75rem' }}
                    >
                      Tentar Novamente
                    </button>
                  </div>
                ) : instagramPostsQuery.isLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    <Loader2 size={24} className="spin" style={{ margin: '0 auto 0.5rem' }} />
                    <p style={{ fontSize: '0.85rem' }}>Carregando grade de posts...</p>
                  </div>
                ) : (instagramPostsQuery.data?.posts || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#64748b' : 'var(--text-muted)', border: isLight ? '1px dashed #cbd5e1' : '1px dashed var(--border-color)', borderRadius: '12px' }}>
                    <Instagram size={32} style={{ opacity: 0.3, margin: '0 auto 0.5rem' }} />
                    <p style={{ fontSize: '0.85rem' }}>Nenhum post agendado para esta campanha ainda. Clique em "Agendar Posts (07h, 13h, 19h)" acima para iniciar os disparos futuros no Horário de Brasília.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {(instagramPostsQuery.data?.posts || []).map((p, idx) => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#ec4899', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>
                            {p.stepOrder || idx + 1}
                          </span>
                          <div>
                            <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem', display: 'block' }}>
                              {p.angleName || `Post ${idx + 1}`} {p.slotTime ? `• ${p.slotTime}` : ''}
                            </strong>
                            <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                              {p.status === 'published' 
                                ? `Publicado em: ${new Date(p.publishedAt || p.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Horário de Brasília)`
                                : `Agendado para: ${new Date(p.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Horário de Brasília)`}
                            </span>
                          </div>
                        </div>
                        <span style={{
                          fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: '20px',
                          background: p.status === 'published' ? (isLight ? '#dcfce7' : 'rgba(16,185,129,0.15)') : p.status === 'failed' ? (isLight ? '#fee2e2' : 'rgba(239,68,68,0.15)') : (isLight ? '#fef3c7' : 'rgba(245,158,11,0.15)'),
                          color: p.status === 'published' ? (isLight ? '#15803d' : '#34d399') : p.status === 'failed' ? (isLight ? '#b91c1c' : '#f87171') : (isLight ? '#b45309' : '#fbbf24')
                        }}>
                          {p.status === 'published' ? '✓ Publicado' : p.status === 'failed' ? '❌ Falha' : '⏳ Agendado'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div></>}
            </div>
          )}

          {activeTab === 'intelligence' && channels.includes('google') && <GoogleCampaignResults campaign={campaign}
            intelligence={intelligenceQuery.data?.google} loading={intelligenceQuery.isLoading} />}
          {activeTab === 'intelligence' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Card de Visão Geral e Ação de Re-análise */}
              <div style={{ 
                background: isLight ? 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.08))' : 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(59,130,246,0.1))', 
                border: isLight ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(16,185,129,0.25)', 
                borderRadius: '16px', 
                padding: '1.5rem', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                flexWrap: 'wrap', 
                gap: '1rem' 
              }}>
                <div>
                  <h3 style={{ color: isLight ? '#064e3b' : '#fff', fontSize: '1.15rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.6rem', margin: 0 }}>
                    <Award size={20} style={{ color: '#10b981' }} /> Loop de Inteligência & Retroalimentação
                  </h3>
                  <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.35rem', lineHeight: '1.4' }}>
                    Resultados de Pesquisa Google, telemetria Meta e engajamento do Instagram alimentam a memória do cliente e as próximas propostas de anúncios.
                  </p>
                </div>
                <button
                  onClick={() => analyzeIntelligenceMutation.mutate()}
                  disabled={analyzeIntelligenceMutation.isPending}
                  className="primary-button"
                  style={{ background: '#10b981', borderColor: '#10b981', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.25rem', fontSize: '0.85rem', color: '#fff', fontWeight: 700 }}
                >
                  {analyzeIntelligenceMutation.isPending ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                  {analyzeIntelligenceMutation.isPending ? 'Processando Inteligência...' : 'Re-analisar e Retroalimentar'}
                </button>
              </div>

              {/* Memória Criativa Viva do Tenant */}
              {memoryQuery.data?.memory && (
                <div className="page-card" style={{ padding: '1.25rem 1.5rem', background: isLight ? '#ffffff' : '#16161d', border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16,185,129,0.2)', borderRadius: '14px', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                  <h4 style={{ color: isLight ? '#047857' : '#34d399', fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                    <Brain size={16} /> Memória Criativa Consolidada do Tenant (Injetada no CGO)
                  </h4>
                  <pre style={{ background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.4)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '1rem', borderRadius: '10px', fontSize: '0.8rem', color: isLight ? '#1e293b' : '#e2e8f0', whiteSpace: 'pre-wrap', lineHeight: '1.5', fontFamily: 'monospace' }}>
                    {memoryQuery.data.memory.creative_insights_prompt || 'Nenhum padrão consolidado ainda.'}
                  </pre>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.8rem', color: isLight ? '#475569' : 'var(--text-muted)' }}>
                    <span>Campanhas Analisadas: <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>{memoryQuery.data.memory.total_campaigns_analyzed || 0}</strong></span>
                    <span>Criativos Mapeados: <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>{memoryQuery.data.memory.total_creatives_analyzed || 0}</strong></span>
                    <span>CTR Médio dos Campeões: <strong style={{ color: isLight ? '#059669' : '#34d399' }}>{memoryQuery.data.memory.avg_best_ctr || 0}%</strong></span>
                    {memoryQuery.data.memory.google && <span>Google Ads: {memoryQuery.data.memory.google.totalCampaigns} campanhas · {memoryQuery.data.memory.google.totalAds} anúncios · {memoryQuery.data.memory.google.totalKeywords} palavras-chave</span>}
                  </div>
                </div>
              )}

              {/* Tabela / Grid de Criativos e Diagnóstico de Performance */}
              {channels.includes('meta') && (
              <div className="page-card" style={{ padding: '1.5rem', background: isLight ? '#ffffff' : '#141418', border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)', borderRadius: '16px', boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none' }}>
                <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BarChart2 size={18} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} /> Desempenho por Ângulo & Nível de Consciência
                </h4>

                {intelligenceQuery.isLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    <Loader2 size={24} className="spin" style={{ margin: '0 auto 0.5rem' }} />
                    <p style={{ fontSize: '0.85rem' }}>Carregando dados de inteligência...</p>
                  </div>
                ) : (intelligenceQuery.data?.items || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#64748b' : 'var(--text-muted)', border: isLight ? '1px dashed #cbd5e1' : '1px dashed var(--border-color)', borderRadius: '12px' }}>
                    <TrendingUp size={32} style={{ opacity: 0.3, margin: '0 auto 0.5rem' }} />
                    <p style={{ fontSize: '0.85rem' }}>Nenhuma análise de performance gerada para esta campanha. Clique em "Re-analisar e Retroalimentar" acima para processar os criativos.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {(intelligenceQuery.data?.items || []).map((item) => {
                      const isChampion = item.performance_rank === 'champion';
                      const isUnder = item.performance_rank === 'underperformer';

                      return (
                        <div key={item.id} style={{
                          padding: '1.25rem',
                          background: isChampion ? (isLight ? '#f0fdf4' : 'rgba(16,185,129,0.04)') : isUnder ? (isLight ? '#fef2f2' : 'rgba(239,68,68,0.03)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)'),
                          border: `1px solid ${isChampion ? (isLight ? '#86efac' : 'rgba(16,185,129,0.3)') : isUnder ? (isLight ? '#fca5a5' : 'rgba(239,68,68,0.3)') : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)')}`,
                          borderRadius: '12px',
                          display: 'flex', flexDirection: 'column', gap: '0.75rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                width: '28px', height: '28px', borderRadius: '50%',
                                background: isChampion ? '#10b981' : isUnder ? '#ef4444' : '#6366f1',
                                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700
                              }}>
                                {item.step_order}
                              </span>
                              <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem' }}>
                                {item.angle_name || `Criativo ${item.step_order}`}
                              </strong>
                              <span style={{ fontSize: '0.75rem', color: isLight ? '#475569' : 'var(--text-muted)', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>
                                {item.consciousness_level}
                              </span>
                            </div>

                            <span style={{
                              fontSize: '0.75rem', fontWeight: 700, padding: '0.25rem 0.75rem', borderRadius: '20px',
                              background: isChampion ? (isLight ? '#dcfce7' : 'rgba(16,185,129,0.2)') : isUnder ? (isLight ? '#fee2e2' : 'rgba(239,68,68,0.2)') : (isLight ? '#fef3c7' : 'rgba(245,158,11,0.2)'),
                              color: isChampion ? (isLight ? '#15803d' : '#34d399') : isUnder ? (isLight ? '#b91c1c' : '#f87171') : (isLight ? '#b45309' : '#fbbf24')
                            }}>
                              {isChampion ? '🏆 CAMPEÃO (Melhor Conversão)' : isUnder ? '⚠️ Abaixo da Média' : '⚡ Competitivo'}
                            </span>
                          </div>

                          <div style={{ fontSize: '0.85rem', color: isLight ? '#334155' : '#cbd5e1', fontStyle: 'italic' }}>
                            "{item.headline}"
                          </div>

                          {/* Métricas de Performance Meta Ads & Orgânico */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.5rem', marginTop: '0.25rem' }}>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Impressões / Alcance</span>
                              <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.88rem' }}>
                                {(item.total_impressions || 0).toLocaleString()} <span style={{ fontSize: '0.72rem', color: isLight ? '#64748b' : 'var(--text-muted)', fontWeight: 400 }}>({(item.total_reach || 0).toLocaleString()})</span>
                              </strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Cliques (Meta)</span>
                              <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.88rem' }}>{(item.total_clicks || 0).toLocaleString()}</strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>CTR (Taxa Cliques)</span>
                              <strong style={{ color: item.ctr_percent > 2 ? (isLight ? '#059669' : '#34d399') : (isLight ? '#0f172a' : '#fff'), fontSize: '0.88rem' }}>{item.ctr_percent}%</strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Leads Meta</span>
                              <strong style={{ color: item.total_leads > 0 ? (isLight ? '#059669' : '#34d399') : (isLight ? '#0f172a' : '#fff'), fontSize: '0.88rem' }}>{item.total_leads}</strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Investimento</span>
                              <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.88rem' }}>
                                R$ {((item.total_spend_micros || 0) / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Gatilhos "QUERO"</span>
                              <strong style={{ color: item.instagram_quero_count > 0 ? '#ec4899' : (isLight ? '#0f172a' : '#fff'), fontSize: '0.88rem' }}>{item.instagram_quero_count}</strong>
                            </div>
                            <div style={{ background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #e2e8f0' : 'none', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                              <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block' }}>Ressonância</span>
                              <strong style={{ color: isLight ? '#4f46e5' : '#818cf8', fontSize: '0.88rem' }}>{item.audience_resonance_score}/100</strong>
                            </div>
                          </div>

                          {/* Racional e Recomendação */}
                          {item.rank_reason && (
                            <p style={{ fontSize: '0.8rem', color: isLight ? '#475569' : 'var(--text-muted)', margin: '0.25rem 0 0 0', lineHeight: '1.4' }}>
                              💡 <strong style={{ color: isLight ? '#0f172a' : '#e2e8f0' }}>Diagnóstico:</strong> {item.rank_reason}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}
            </div>
          )}

        </div>

        {/* Drawer Footer com Navegação Unificada do Fluxo (Stepper & Ações) */}
        <div style={{ 
          padding: '1rem 1.75rem', 
          borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', 
          background: isLight ? '#f8fafc' : '#121216', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          {/* Lado Esquerdo: Navegação de Retorno ou Indicador de Etapa */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {activeTab !== 'briefing' ? (
              <button
                type="button"
                onClick={() => {
                  if (activeTab === 'strategy') setActiveTab('briefing');
                  else if (activeTab === 'video-studio') setActiveTab('strategy');
                  else if (activeTab === 'instagram') setActiveTab('video-studio');
                  else if (activeTab === 'intelligence') setActiveTab('instagram');
                }}
                className="secondary-button"
                style={{
                  fontSize: '0.82rem',
                  padding: '0.55rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  background: isLight ? '#ffffff' : 'transparent',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)',
                  color: isLight ? '#334155' : 'inherit'
                }}
              >
                <ChevronLeft size={15} />
                <span>
                  {activeTab === 'strategy' && 'Voltar ao Briefing'}
                  {activeTab === 'video-studio' && 'Voltar à Matriz de Anúncios'}
                  {activeTab === 'instagram' && 'Voltar ao Estúdio de Vídeo'}
                  {activeTab === 'intelligence' && 'Voltar à Publicação'}
                </span>
              </button>
            ) : (
              <span style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Target size={14} style={{ color: '#6366f1' }} />
                <span>Etapa 1 de 4 • Briefing, canais e contas da campanha</span>
              </span>
            )}
          </div>

          {/* Lado Direito: Ações Contextuais da Etapa Ativa */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {/* ETAPA 1: BRIEFING */}
            {activeTab === 'briefing' && (
              <button
                type="button"
                onClick={() => setActiveTab('strategy')}
                className="primary-button"
                style={{
                  padding: '0.65rem 1.35rem',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  boxShadow: '0 3px 12px rgba(99,102,241,0.25)',
                  cursor: 'pointer'
                }}
              >
                <span>Avançar para revisão dos anúncios</span>
                <ChevronRight size={15} />
              </button>
            )}

            {/* ETAPA 2: ESTÚDIO CRIATIVO (MCA) */}
            {activeTab === 'strategy' && (
              <>
                {channels.includes('meta') && <button
                  type="button"
                  onClick={() => notifyWhatsAppMutation.mutate()}
                  disabled={notifyWhatsAppMutation.isPending}
                  className="secondary-button"
                  style={{
                    padding: '0.65rem 1.15rem',
                    fontSize: '0.82rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    borderColor: isLight ? '#059669' : '#10b981',
                    color: isLight ? '#047857' : '#34d399',
                    background: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.08)',
                    fontWeight: 600,
                    borderRadius: '8px'
                  }}
                  title="Enviar resumo das 5 propostas para o WhatsApp do Gestor/Cliente"
                >
                  {notifyWhatsAppMutation.isPending ? <Loader2 size={15} className="spin" /> : <MessageCircle size={15} />}
                  {notifyWhatsAppMutation.isPending ? 'Enviando...' : 'Solicitar Aprovação (WhatsApp)'}
                </button>}

                <button
                  type="button"
                  onClick={() => setActiveTab('video-studio')}
                  className="secondary-button"
                  style={{
                    padding: '0.65rem 1.15rem',
                    fontSize: '0.82rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    borderColor: '#3b82f6',
                    color: isLight ? '#1d4ed8' : '#93c5fd',
                    background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.1)',
                    fontWeight: 600,
                    borderRadius: '8px'
                  }}
                  title="Produzir vídeos cinematográficos a partir dos 5 níveis"
                >
                  <Film size={15} style={{ color: '#3b82f6' }} />
                  <span>Estúdio de Vídeo</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('instagram')}
                  className="primary-button"
                  style={{
                    padding: '0.65rem 1.35rem',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ec4899, #d946ef)',
                    border: 'none',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    boxShadow: '0 3px 12px rgba(236,72,153,0.25)',
                    cursor: 'pointer'
                  }}
                >
                  <span>Avançar para aprovação e publicação</span>
                  <ChevronRight size={15} />
                </button>
              </>
            )}

            {/* ETAPA ESTÚDIO DE VÍDEO */}
            {activeTab === 'video-studio' && (
              <button
                type="button"
                onClick={() => setActiveTab('instagram')}
                className="primary-button"
                style={{
                  padding: '0.65rem 1.35rem',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ec4899, #d946ef)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  boxShadow: '0 3px 12px rgba(236,72,153,0.25)',
                  cursor: 'pointer'
                }}
              >
                <span>Avançar para aprovação e publicação</span>
                <ChevronRight size={15} />
              </button>
            )}

            {/* ETAPA 3: PUBLICAÇÃO & CANAIS */}
            {activeTab === 'instagram' && (
              <>
                {currentStatus === 'em_revisao' && channels.includes('meta') && (
                  <button
                    type="button"
                    onClick={() => notifyWhatsAppMutation.mutate()}
                    disabled={notifyWhatsAppMutation.isPending}
                    className="secondary-button"
                    style={{
                      padding: '0.65rem 1.15rem',
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      borderColor: isLight ? '#059669' : '#10b981',
                      color: isLight ? '#047857' : '#34d399',
                      background: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.08)',
                      fontWeight: 600,
                      borderRadius: '8px'
                    }}
                    title="Enviar prévia para o WhatsApp do gestor antes de publicar"
                  >
                    {notifyWhatsAppMutation.isPending ? <Loader2 size={15} className="spin" /> : <MessageCircle size={15} />}
                    {notifyWhatsAppMutation.isPending ? 'Enviando...' : 'Solicitar Aprovação (WhatsApp)'}
                  </button>
                )}

                <button type="button" className="campaign-journey-button" onClick={() => setActiveTab('intelligence')}>
                  Acompanhar resultados
                </button>
              </>
            )}

            {/* ETAPA 4: TELEMETRIA & INTELIGÊNCIA */}
            {activeTab === 'intelligence' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                    queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'intelligence'] });
                  }}
                  disabled={intelligenceQuery.isFetching}
                  className="secondary-button"
                  style={{
                    padding: '0.65rem 1.15rem',
                    fontSize: '0.82rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    borderColor: '#1877f2',
                    color: '#1877f2',
                    background: isLight ? '#eff6ff' : 'rgba(24,119,242,0.08)',
                    fontWeight: 600,
                    borderRadius: '8px'
                  }}
                >
                  <RefreshCw size={15} className={intelligenceQuery.isFetching ? 'spin' : ''} />
                  <span>{intelligenceQuery.isFetching ? 'Atualizando...' : 'Atualizar resultados'}</span>
                </button>

                {['ativa', 'running', 'active', 'publicada'].includes(currentStatus) && (
                  <button
                    type="button"
                    onClick={() => transitionMutation.mutate('pausada')}
                    disabled={transitionMutation.isPending}
                    className="secondary-button"
                    style={{
                      padding: '0.65rem 1.15rem',
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      borderColor: '#f59e0b',
                      color: isLight ? '#d97706' : '#fbbf24',
                      background: isLight ? '#fffbeb' : 'rgba(245,158,11,0.08)',
                      borderRadius: '8px'
                    }}
                  >
                    <Pause size={15} />
                    <span>Pausar Campanha</span>
                  </button>
                )}

                {['pausada', 'paused'].includes(currentStatus) && (
                  <button
                    type="button"
                    onClick={() => transitionMutation.mutate('ativa')}
                    disabled={transitionMutation.isPending}
                    className="primary-button"
                    style={{
                      padding: '0.65rem 1.15rem',
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      background: '#10b981',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 700,
                      borderRadius: '8px'
                    }}
                  >
                    <Play size={15} />
                    <span>Retomar Campanha</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

      </div>

      {/* MODAL DE REESTRUTURAÇÃO MCA COM IA */}
      {isUpgradeModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: isLight ? 'rgba(15,23,42,0.5)' : 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="page-card animate-in" style={{ width: '90%', maxWidth: '540px', background: isLight ? '#ffffff' : '#141418', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', borderRadius: '20px', padding: '2rem', boxShadow: isLight ? '0 20px 40px rgba(0,0,0,0.1)' : '0 20px 40px rgba(0,0,0,0.8)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: isLight ? '#0f172a' : 'var(--text-color)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Sparkles size={20} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} /> Reestruturar Campanha com MCA
              </h3>
              <button onClick={() => setIsUpgradeModalOpen(false)} style={{ background: 'none', border: 'none', color: isLight ? '#64748b' : 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '0.85rem', color: isLight ? '#475569' : 'var(--text-muted)', marginBottom: '1.5rem' }}>
              O Chief Growth Officer (CGO) irá reescrever a estratégia completa da campanha "{campaign.name}" gerando 5 anúncios alinhados aos níveis de consciência de Eugene Schwartz.
            </p>

            <form onSubmit={handleExecuteUpgrade} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : 'inherit' }}>Nicho ou Segmento de Mercado</label>
                <input 
                  type="text" 
                  placeholder="Ex: SaaS B2B, Odontologia, Consultoria Tributária"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#1c1c22', color: isLight ? '#0f172a' : '#fff' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : 'inherit' }}>Principais Dores dos Clientes</label>
                <textarea 
                  rows={3}
                  placeholder="Ex: Atendimento lento, perda de leads no WhatsApp, falta de previsibilidade comercial"
                  value={painPoints}
                  onChange={(e) => setPainPoints(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#1c1c22', color: isLight ? '#0f172a' : '#fff' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : 'inherit' }}>Tom de Voz da Copy</label>
                <select 
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#1c1c22', color: isLight ? '#0f172a' : '#fff' }}
                >
                  <option value="persuasivo_autoridade">Autoridade & Alta Conversão (Direto ao ponto)</option>
                  <option value="urgente_escassez">Urgência & Escassez (Ofertas limitadas)</option>
                  <option value="educativo_consultivo">Educativo & Consultivo (Quebra de crenças)</option>
                  <option value="descontraido_moderno">Descontraído & Moderno (Engajamento jovem)</option>
                </select>
              </div>

              {/* Opção de Preservação de Imagens */}
              <div 
                style={{ 
                  background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', 
                  padding: '0.85rem 1rem', 
                  borderRadius: '8px', 
                  border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '0.75rem', 
                  cursor: 'pointer' 
                }} 
                onClick={() => setKeepImagesOnUpgrade(!keepImagesOnUpgrade)}
              >
                <input 
                  type="checkbox" 
                  checked={keepImagesOnUpgrade} 
                  onChange={(e) => setKeepImagesOnUpgrade(e.target.checked)}
                  style={{ marginTop: '0.2rem', accentColor: 'var(--primary-color)', cursor: 'pointer' }}
                />
                <div>
                  <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem', display: 'block' }}>
                    🖼️ Preservar artes e imagens já sintetizadas no R2
                  </strong>
                  <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    {keepImagesOnUpgrade 
                      ? 'As imagens já salvas nos 5 passos serão mantidas intactas. Apenas as copies, headlines e ângulos serão reformulados.' 
                      : '⚠️ Novas imagens serão sintetizadas do zero pela IA substituindo as atuais.'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button 
                  type="button" 
                  onClick={() => setIsUpgradeModalOpen(false)}
                  className="secondary-button" 
                  style={{ flex: 1, padding: '0.75rem', background: isLight ? '#ffffff' : 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', color: isLight ? '#334155' : 'inherit' }}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={upgradeMCAMutation.isPending}
                  className="primary-button" 
                  style={{ flex: 2, padding: '0.75rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', color: '#fff', fontWeight: 700 }}
                >
                  {upgradeMCAMutation.isPending ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
                  {upgradeMCAMutation.isPending ? 'Executando CGO...' : 'Reestruturar com IA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE IMAGEM AMPLIADA & GALERIA DE VERSÕES ANTERIORES */}
      {zoomedCreative && (
        <div 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            zIndex: 100000, 
            background: isLight ? 'rgba(15,23,42,0.7)' : 'rgba(0, 0, 0, 0.88)', 
            backdropFilter: 'blur(10px)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '1.5rem'
          }}
          onClick={() => {
            setZoomedCreative(null);
            setSelectedZoomUrl(null);
          }}
        >
          <div 
            className="animate-in"
            style={{ 
              background: isLight ? '#ffffff' : '#121216', 
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)', 
              borderRadius: '16px', 
              maxWidth: '860px', 
              width: '100%', 
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: isLight ? '0 25px 50px -12px rgba(15, 23, 42, 0.25)' : '0 25px 60px -15px rgba(0, 0, 0, 0.9)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header da Imagem Ampliada */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '6px', background: isLight ? '#e0e7ff' : 'rgba(99, 102, 241, 0.15)', color: isLight ? '#3730a3' : '#818cf8', textTransform: 'uppercase' }}>
                    Passo {zoomedCreative.stepOrder || 1} • {zoomedCreative.consciousnessLevel?.replace('_', ' ')}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    {zoomedCreative.angleName}
                  </span>
                </div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: isLight ? '#0f172a' : '#fff', fontWeight: 700 }}>
                  {zoomedCreative.headline}
                </h3>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {zoomedCreative.videoUrl && (
                  <div style={{ display: 'flex', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)', padding: '0.2rem', borderRadius: '8px', gap: '0.2rem' }}>
                    <button
                      onClick={() => setZoomMediaTab('image')}
                      style={{
                        padding: '0.35rem 0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        background: zoomMediaTab === 'image' ? (isLight ? '#ffffff' : '#3b82f6') : 'transparent',
                        color: zoomMediaTab === 'image' ? (isLight ? '#0f172a' : '#ffffff') : (isLight ? '#64748b' : 'var(--text-muted)'),
                        boxShadow: zoomMediaTab === 'image' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                      }}
                    >
                      🖼️ Imagem
                    </button>
                    <button
                      onClick={() => setZoomMediaTab('video')}
                      style={{
                        padding: '0.35rem 0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        background: zoomMediaTab === 'video' ? (isLight ? '#ffffff' : '#a855f7') : 'transparent',
                        color: zoomMediaTab === 'video' ? (isLight ? '#6d28d9' : '#ffffff') : (isLight ? '#64748b' : 'var(--text-muted)'),
                        boxShadow: zoomMediaTab === 'video' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                      }}
                    >
                      🎬 Vídeo Motion
                    </button>
                  </div>
                )}

                <button 
                  onClick={() => {
                    setZoomedCreative(null);
                    setSelectedZoomUrl(null);
                    setZoomMediaTab('image');
                  }}
                  style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#334155' : '#fff', cursor: 'pointer' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Conteúdo Central: Imagem Grande ou Vídeo & Overlay */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'center' }}>
              {zoomMediaTab === 'video' && zoomedCreative.videoUrl ? (
                <div style={{ position: 'relative', width: '100%', maxWidth: '360px', aspectRatio: '9/16', background: '#09090b', borderRadius: '14px', overflow: 'hidden', border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(168,85,247,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 16px 40px rgba(0,0,0,0.5)' }}>
                  <video 
                    key={zoomedCreative.videoUrl}
                    src={zoomedCreative.videoUrl} 
                    controls 
                    autoPlay 
                    loop 
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  
                  {/* Badge Vídeo 9:16 Reels / Stories */}
                  <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(168,85,247,0.9)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '6px', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Film size={12} /> REELS / STORIES (9:16)
                  </div>

                  <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '6px' }}>
                    <button 
                      type="button"
                      disabled={!!downloadingVideoFormat}
                      onClick={() => handleDownloadVideo(zoomedCreative.videoUrl!, zoomedCreative.id, 'mp4')}
                      style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '0.3rem 0.6rem', borderRadius: '6px', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: '0.3rem', border: 'none', cursor: downloadingVideoFormat ? 'wait' : 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
                    >
                      <Download size={12} /> {downloadingVideoFormat === 'mp4' ? (downloadingVideoProgress || 'Baixando...') : 'Baixar MP4'}
                    </button>
                    <button 
                      type="button"
                      disabled={!!downloadingVideoFormat}
                      onClick={() => handleDownloadVideo(zoomedCreative.videoUrl!, zoomedCreative.id, 'webm')}
                      style={{ background: 'rgba(0,0,0,0.7)', color: '#d4d4d8', fontSize: '0.7rem', fontWeight: 600, padding: '0.3rem 0.6rem', borderRadius: '6px', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: '0.3rem', border: 'none', cursor: downloadingVideoFormat ? 'wait' : 'pointer' }}
                    >
                      {downloadingVideoFormat === 'webm' ? (downloadingVideoProgress || 'Baixando...') : 'WebM'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative', width: '100%', maxWidth: '520px', aspectRatio: '1/1', background: '#09090b', borderRadius: '12px', overflow: 'hidden', border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(0,0,0,0.4)' }}>
                  {selectedZoomUrl || zoomedCreative.assetUrl ? (
                    <img 
                      src={selectedZoomUrl || zoomedCreative.assetUrl} 
                      alt="Arte Ampliada" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} 
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                      <Image size={54} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                      <p style={{ margin: 0, color: '#fff', fontSize: '0.95rem' }}>Nenhuma imagem gerada para este criativo ainda</p>
                      <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>Clique em "Sintetizar Nova Arte" abaixo para criar com IA</p>
                    </div>
                  )}

                  {/* Overlay Text estampado na arte */}
                  {zoomedCreative.overlayText && (selectedZoomUrl || zoomedCreative.assetUrl) && (
                    <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', padding: '0.6rem 1rem', borderRadius: '8px', borderLeft: '4px solid #fbbf24', color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.02em', textAlign: 'center' }}>
                      {zoomedCreative.overlayText}
                    </div>
                  )}
                </div>
              )}

              {/* Botão de Definir como Imagem Ativa & Botão de Excluir do R2 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', maxWidth: '520px' }}>
                {selectedZoomUrl && selectedZoomUrl !== zoomedCreative.assetUrl && (
                  <div style={{ background: isLight ? '#eef2ff' : 'rgba(99, 102, 241, 0.12)', border: isLight ? '1px solid #c7d2fe' : '1px solid rgba(99, 102, 241, 0.3)', padding: '0.75rem 1.25rem', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.85rem', color: isLight ? '#3730a3' : '#c7d2fe', fontWeight: 600 }}>
                      Esta versão histórica não está ativa.
                    </span>
                    <button
                      onClick={() => {
                        restoreImageMutation.mutate(
                          { creativeId: zoomedCreative.id, assetUrl: selectedZoomUrl },
                          {
                            onSuccess: () => {
                              setZoomedCreative({ ...zoomedCreative, assetUrl: selectedZoomUrl });
                            }
                          }
                        );
                      }}
                      disabled={restoreImageMutation.isPending}
                      className="primary-button"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700, color: '#fff' }}
                    >
                      {restoreImageMutation.isPending ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                      Definir como Imagem Ativa
                    </button>
                  </div>
                )}

                {/* Botão de Posicionar Logo & Excluir Imagem do R2 */}
                {(selectedZoomUrl || zoomedCreative.assetUrl) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        const targetUrl = selectedZoomUrl || zoomedCreative.assetUrl;
                        if (targetUrl) {
                          setLogoPositioningCreative({
                            ...zoomedCreative,
                            assetUrl: targetUrl
                          });
                        }
                      }}
                      className="primary-button"
                      style={{ 
                        padding: '0.45rem 0.9rem', 
                        fontSize: '0.8rem', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.4rem', 
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', 
                        border: 'none', 
                        color: '#fff',
                        fontWeight: 600
                      }}
                      title="Abrir Canvas para movimentar e redimensionar a logo sobre esta imagem"
                    >
                      <Move size={14} /> Posicionar Logo Nesta Imagem
                    </button>

                    <button
                      onClick={async () => {
                        const targetUrl = selectedZoomUrl || zoomedCreative.assetUrl;
                        if (confirm('Deseja realmente excluir permanentemente esta imagem do Cloudflare R2?')) {
                          await deleteImageMutation.mutateAsync({ creativeId: zoomedCreative.id, assetUrl: targetUrl || undefined });
                          const updatedList = await campaignsApi.getCreatives(campaign.id, init);
                          const updated = updatedList.items.find(c => c.id === zoomedCreative.id);
                          if (updated) {
                            setZoomedCreative(updated);
                            setSelectedZoomUrl(updated.assetUrl || null);
                          } else {
                            setZoomedCreative(null);
                            setSelectedZoomUrl(null);
                          }
                        }
                      }}
                      disabled={deleteImageMutation.isPending}
                      className="secondary-button"
                      style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: '#ef4444', borderColor: isLight ? '#fca5a5' : 'rgba(239, 68, 68, 0.3)', background: isLight ? '#fef2f2' : 'transparent', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                      title="Excluir imagem exibida do Cloudflare R2"
                    >
                      {deleteImageMutation.isPending ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
                      Excluir esta Imagem do R2
                    </button>
                  </div>
                )}
              </div>

              {/* Metáfora Visual & Edição Inline antes da Geração */}
              <div style={{ width: '100%', maxWidth: '640px', background: isLight ? '#f0f9ff' : 'rgba(56, 189, 248, 0.04)', padding: '0.75rem 1rem', borderRadius: '8px', border: isLight ? '1px solid #bae6fd' : '1px solid rgba(56, 189, 248, 0.15)', fontSize: '0.8rem', color: isLight ? '#334155' : '#cbd5e1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <strong style={{ color: isLight ? '#0284c7' : '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    💡 Metáfora Visual (Diretiva da Cena para a IA)
                  </strong>
                  <span style={{ fontSize: '0.7rem', color: isLight ? '#64748b' : '#94a3b8' }}>
                    Ajuste fino antes de sintetizar
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <textarea
                    rows={2}
                    value={zoomedCreative.visualMetaphor || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setZoomedCreative({ ...zoomedCreative, visualMetaphor: val });
                    }}
                    onBlur={async () => {
                      await campaignsApi.updateCreative(campaign.id, zoomedCreative.id, {
                        visualMetaphor: zoomedCreative.visualMetaphor
                      });
                      toast.success('Metáfora visual atualizada para geração.');
                    }}
                    placeholder="Defina a metáfora visual e regras da cena para a IA renderizar..."
                    style={{
                      flex: 1,
                      padding: '0.45rem 0.65rem',
                      borderRadius: '6px',
                      border: isLight ? '1px solid #7dd3fc' : '1px solid rgba(56, 189, 248, 0.3)',
                      background: isLight ? '#ffffff' : '#0f172a',
                      color: isLight ? '#0f172a' : '#f8fafc',
                      fontSize: '0.8rem',
                      lineHeight: '1.4'
                    }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      await campaignsApi.updateCreative(campaign.id, zoomedCreative.id, {
                        visualMetaphor: zoomedCreative.visualMetaphor
                      });
                      toast.success('Metáfora visual salva.');
                    }}
                    className="secondary-button"
                    style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    title="Salvar alteração da metáfora"
                  >
                    <Save size={12} /> Salvar
                  </button>
                </div>
              </div>
            </div>

            {/* Galeria de Versões Anteriores & Ações de Geração */}
            <div style={{ padding: '1.25rem 1.5rem', background: isLight ? '#f8fafc' : '#0e0e11', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Layers size={16} style={{ color: isLight ? '#4f46e5' : 'var(--primary-color)' }} />
                  <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}>
                    Galeria de Versões Deste Criativo ({zoomedCreative.imageHistory?.length || (zoomedCreative.assetUrl ? 1 : 0)})
                  </strong>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {/* Botão de geração de filme com IA */}
                  <button
                    onClick={() => openMovieGeneration(zoomedCreative.id)}
                    disabled={generateMovieMutation.isPending}
                    className="primary-button"
                    style={{ padding: '0.45rem 0.95rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #ec4899, #8b5cf6)', border: 'none', boxShadow: '0 4px 12px rgba(236, 72, 153, 0.35)' }}
                    title="Gera um filme publicitário com IA generativa de vídeo"
                  >
                    {generateMovieMutation.isPending ? <Loader2 size={13} className="spin" /> : <Film size={13} />}
                    🎬 Gerar Filme com IA (Kling/Minimax)
                  </button>

                  {/* Botão para levar ao Estúdio de Vídeo Dedicado */}
                  <button
                    type="button"
                    onClick={() => {
                      setZoomedCreative(null);
                      setSelectedStudioCreativeId(zoomedCreative.id);
                      setActiveTab('video-studio');
                    }}
                    className="primary-button"
                    style={{
                      padding: '0.45rem 0.95rem',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      fontWeight: 700,
                      color: '#fff',
                      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)'
                    }}
                    title="Abrir este criativo no Estúdio de Vídeo dedicado"
                  >
                    <Film size={14} />
                    🎬 Produzir no Estúdio de Vídeo
                  </button>

                  {/* Botão de Geração Motion Express no Navegador */}
                  <button
                    onClick={async () => {
                      await generateVideoMutation.mutateAsync({ creativeId: zoomedCreative.id, format: 'vertical' });
                      const updatedList = await campaignsApi.getCreatives(campaign.id, init);
                      const updated = updatedList.items.find(c => c.id === zoomedCreative.id);
                      if (updated) {
                        setZoomedCreative(updated);
                        setZoomMediaTab('video');
                      }
                    }}
                    disabled={generateVideoMutation.isPending}
                    className="secondary-button"
                    style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', color: isLight ? '#334155' : '#cbd5e1' }}
                    title="Gera vídeo com motion 3-atos a 30 FPS diretamente no navegador"
                  >
                    {generateVideoMutation.isPending ? <Loader2 size={13} className="spin" /> : <Zap size={13} />}
                    ⚡ Motion Express
                  </button>

                  {/* Botão de Chave de Vídeo IA */}
                  <button
                    type="button"
                    onClick={() => setIsVideoKeyModalOpen(true)}
                    className="secondary-button"
                    style={{ padding: '0.45rem 0.65rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', background: videoKeyQuery.data?.configured ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', color: videoKeyQuery.data?.configured ? '#10b981' : '#fbbf24', border: videoKeyQuery.data?.configured ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)' }}
                    title="Configurar chave de API de Vídeo IA (Fal.ai, Replicate ou OmniRouter)"
                  >
                    🔑 {videoKeyQuery.data?.configured ? `Vídeo IA (${videoKeyQuery.data.provider})` : 'Configurar Chave Vídeo'}
                  </button>

                  <label className="secondary-button" style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', background: isLight ? '#ffffff' : 'transparent', border: '1px solid #6366f1', color: isLight ? '#4f46e5' : '#818cf8', fontWeight: 600 }} title="Fazer upload de vídeo pronto (.mp4 / .mov / .webm)">
                    {uploadVideoMutation.isPending && (uploadVideoMutation.variables as any)?.creativeId === zoomedCreative.id ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                    Upload Vídeo (.mp4)
                    <input 
                      type="file" 
                      accept="video/mp4,video/quicktime,video/webm" 
                      style={{ display: 'none' }} 
                      onChange={(e) => {
                        handleVideoUpload(zoomedCreative.id, e);
                      }} 
                      disabled={uploadVideoMutation.isPending} 
                    />
                  </label>

                  <label className="secondary-button" style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', background: isLight ? '#ffffff' : 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', color: isLight ? '#334155' : 'inherit' }}>
                    {uploadImageMutation.isPending && uploadImageMutation.variables?.creativeId === zoomedCreative.id ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                    Upload Imagem
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={async (e) => {
                        await handleFileUpload(zoomedCreative.id, e);
                      }} 
                      disabled={uploadImageMutation.isPending} 
                    />
                  </label>

                  {/* Botão de Gerar Arte (Feed 1:1) */}
                  <button
                    onClick={async () => {
                      await generateImageMutation.mutateAsync({ creativeId: zoomedCreative.id, format: 'feed' });
                      const updatedList = await campaignsApi.getCreatives(campaign.id, init);
                      const updated = updatedList.items.find(c => c.id === zoomedCreative.id);
                      if (updated) {
                        setZoomedCreative(updated);
                        setSelectedZoomUrl(updated.assetUrl || null);
                      }
                    }}
                    disabled={generateImageMutation.isPending}
                    className="secondary-button"
                    style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    title="Gera a arte no formato Feed (1:1)"
                  >
                    {generateImageMutation.isPending ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                    🔲 Feed (1:1)
                  </button>

                  {/* Botão de Gerar Arte (Stories 9:16) */}
                  <button
                    onClick={async () => {
                      await generateImageMutation.mutateAsync({ creativeId: zoomedCreative.id, format: 'stories' });
                      const updatedList = await campaignsApi.getCreatives(campaign.id, init);
                      const updated = updatedList.items.find(c => c.id === zoomedCreative.id);
                      if (updated) {
                        setZoomedCreative(updated);
                        setSelectedZoomUrl(updated.assetUrl || null);
                      }
                    }}
                    disabled={generateImageMutation.isPending}
                    className="secondary-button"
                    style={{ padding: '0.45rem 0.75rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    title="Gera a arte no formato Stories (9:16)"
                  >
                    {generateImageMutation.isPending ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                    📱 Stories (9:16)
                  </button>
                </div>
              </div>

              {/* Miniaturas da Galeria */}
              {zoomedCreative.imageHistory && zoomedCreative.imageHistory.length > 0 ? (
                <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                  {zoomedCreative.imageHistory.map((hist, hIdx) => {
                    const isViewing = (selectedZoomUrl || zoomedCreative.assetUrl) === hist.url;
                    const isActiveOnCreative = zoomedCreative.assetUrl === hist.url;

                    return (
                      <div
                        key={hist.id || hIdx}
                        onClick={() => setSelectedZoomUrl(hist.url)}
                        style={{
                          position: 'relative',
                          width: '74px',
                          height: '74px',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          border: isViewing ? '2px solid #38bdf8' : isActiveOnCreative ? '2px solid #10b981' : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                          cursor: 'pointer',
                          flexShrink: 0,
                          background: '#000',
                          transition: 'all 0.2s ease',
                          transform: isViewing ? 'scale(1.05)' : 'scale(1)',
                          boxShadow: isViewing ? '0 0 12px rgba(56, 189, 248, 0.4)' : 'none'
                        }}
                      >
                        <img 
                          src={hist.url} 
                          alt={`Versão ${hIdx + 1}`} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />

                        {/* Botão de excluir miniatura individual da galeria */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (confirm(`Deseja realmente excluir a versão ${hIdx + 1} permanentemente do Cloudflare R2?`)) {
                              await deleteImageMutation.mutateAsync({ creativeId: zoomedCreative.id, assetUrl: hist.url });
                              const updatedList = await campaignsApi.getCreatives(campaign.id, init);
                              const updated = updatedList.items.find(c => c.id === zoomedCreative.id);
                              if (updated) {
                                setZoomedCreative(updated);
                                setSelectedZoomUrl(updated.assetUrl || null);
                              }
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '3px',
                            right: '3px',
                            background: 'rgba(239, 68, 68, 0.92)',
                            border: 'none',
                            borderRadius: '5px',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            cursor: 'pointer',
                            zIndex: 10,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.6)'
                          }}
                          title="Excluir esta versão do Cloudflare R2"
                        >
                          <Trash2 size={11} />
                        </button>

                        {isActiveOnCreative && (
                          <div style={{ position: 'absolute', bottom: '3px', left: '3px', background: '#10b981', color: '#fff', fontSize: '0.55rem', fontWeight: 800, padding: '0.1rem 0.3rem', borderRadius: '4px' }}>
                            EM USO
                          </div>
                        )}

                        {isViewing && !isActiveOnCreative && (
                          <div style={{ position: 'absolute', bottom: '3px', right: '3px', background: '#38bdf8', color: '#000', fontSize: '0.55rem', fontWeight: 800, padding: '0.1rem 0.3rem', borderRadius: '4px' }}>
                            VER
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center', padding: '0.5rem' }}>
                  Nenhuma outra versão gerada anteriormente para este passo.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <VideoGenerationModal
        open={isVideoGenerationModalOpen}
        provider={videoGenerationProvider}
        statuses={videoKeyQuery.data?.providers || []}
        duration={videoDuration}
        onDurationChange={setVideoDuration}
        prompt={videoPrompt}
        loadingPrompt={previewMoviePromptMutation.isPending}
        generating={generateMovieMutation.isPending}
        isLight={isLight}
        onProviderChange={setVideoGenerationProvider}
        onPromptChange={setVideoPrompt}
        onConfigure={() => {
          setVideoApiProvider(videoGenerationProvider);
          setIsVideoKeyModalOpen(true);
        }}
        onCancel={() => {
          setIsVideoGenerationModalOpen(false);
          setPendingVideoCreativeId(null);
        }}
        onGenerate={() => {
          if (!pendingVideoCreativeId || !videoPrompt.trim()) return;
          generateMovieMutation.mutate({
            creativeId: pendingVideoCreativeId,
            format: 'vertical',
            provider: videoGenerationProvider,
            prompt: videoPrompt.trim()
          });
        }}
      />

      <VideoStudioPanel
        isOpen={isVideoStudioOpen}
        onClose={() => {
          setIsVideoStudioOpen(false);
          setVideoStudioCreative(null);
        }}
        campaignId={campaign.id}
        creativeId={videoStudioCreative?.id || ''}
        creativeTitle={videoStudioCreative?.title || ''}
        onVideoApplied={async () => {
          await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'creatives'] });
          await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        }}
      />

      {/* Modal de Configuração de Chave de Vídeo IA (Fal.ai / Replicate / OmniRouter) */}
      {isVideoKeyModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: isLight ? '#ffffff' : '#18181b', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '480px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Film size={20} style={{ color: '#ec4899' }} />
                <h3 style={{ margin: 0, color: isLight ? '#0f172a' : '#fff', fontSize: '1.1rem', fontWeight: 700 }}>
                  Chave de API para Vídeo IA Generativo
                </h3>
              </div>
              <button 
                onClick={() => setIsVideoKeyModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#64748b' : '#94a3b8', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: '0.85rem', color: isLight ? '#475569' : '#94a3b8', lineHeight: 1.5, marginBottom: '1.25rem' }}>
              Insira sua chave de API para gerar <strong>filmes cinematográficos</strong> (Kling, Minimax ou Luma) com os avatares da marca atuando no cenário.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.35rem' }}>
                  Provedor de Vídeo Generativo
                </label>
                <select
                  value={videoApiProvider}
                  onChange={(e) => setVideoApiProvider(e.target.value as any)}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#27272a', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                >
                  <option value="fal">Fal.ai (Kling AI 1.5 / Minimax / Wan 2.1 - Recomendado)</option>
                  <option value="replicate">Replicate (Kling / Minimax Video)</option>
                  <option value="omnirouter">OmniRouter Video Generations</option>
                  <option value="kling">Kling direto (Video 3.0 Omni 4K)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.35rem' }}>
                  Chave de API ({videoApiProvider === 'fal' ? 'FAL_KEY' : videoApiProvider === 'replicate' ? 'REPLICATE_API_TOKEN' : videoApiProvider === 'kling' ? 'KLING_API_KEY' : 'OMNIROUTER_API_KEY'})
                </label>
                <input
                  type="password"
                  value={videoApiKeyInput}
                  onChange={(e) => setVideoApiKeyInput(e.target.value)}
                  placeholder={selectedVideoKeyStatus?.configured ? `Configurada (${selectedVideoKeyStatus.maskedKey})` : 'Cole sua chave aqui...'}
                  style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#27272a', color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem' }}
                />
                {videoApiProvider === 'replicate' && (
                  <small>Use o token da conta Replicate: 40 caracteres, iniciado por r8_.</small>
                )}
                {videoApiProvider === 'kling' && (
                  <small>Use a API Key criada no console oficial do Kling.</small>
                )}
              </div>

              {selectedVideoKeyStatus?.configured && (
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '0.6rem 0.85rem', borderRadius: '8px', fontSize: '0.8rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Check size={14} /> Chave ativa no tenant ({selectedVideoKeyStatus.provider}: {selectedVideoKeyStatus.maskedKey})
                </div>
              )}

              {selectedVideoKeyStatus?.invalidReason && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '0.6rem 0.85rem', borderRadius: '8px', fontSize: '0.8rem', color: '#ef4444' }}>
                  {selectedVideoKeyStatus.invalidReason}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setIsVideoKeyModalOpen(false)}
                  className="secondary-button"
                  style={{ padding: '0.6rem 1rem', fontSize: '0.85rem' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!videoApiKeyInput.trim()) {
                      alert('Por favor, informe a chave de API');
                      return;
                    }
                    saveVideoKeyMutation.mutate({ apiKey: videoApiKeyInput.trim(), provider: videoApiProvider });
                  }}
                  disabled={saveVideoKeyMutation.isPending || !videoApiKeyInput.trim()}
                  className="primary-button"
                  style={{ padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 700, background: 'linear-gradient(135deg, #ec4899, #8b5cf6)', border: 'none', color: '#fff' }}
                >
                  {saveVideoKeyMutation.isPending ? <Loader2 size={15} className="spin" /> : <Save size={15} />}
                  Salvar Chave
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Publicação de Stories com Escolha de Step e Imagem */}
      {isStoryModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(8px)' }}>
          <div style={{ background: isLight ? '#ffffff' : '#141418', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', borderRadius: '20px', padding: '1.75rem', width: '100%', maxWidth: '780px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #ec4899, #f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <Instagram size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: isLight ? '#0f172a' : '#fff', fontSize: '1.15rem', fontWeight: 700 }}>
                    Publicar Story no Instagram
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    Escolha qual Step da campanha e qual imagem da galeria você deseja publicar
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setIsStoryModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#64748b' : '#94a3b8', cursor: 'pointer', padding: '0.4rem', borderRadius: '8px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* 1. Seletor de Steps (1 a 5) */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                1. Selecione o Step da Campanha:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.5rem' }}>
                {creatives.map((c, idx) => {
                  const stepNum = c.stepOrder || idx + 1;
                  const isSelected = storySelectedStep === stepNum;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setStorySelectedStep(stepNum);
                        setStorySelectedImageUrl(c.assetUrl || null);
                        setStoryCustomCaption(`🔥 ${c.headline}\n\n${c.primaryText || ''}\n\n💬 Responda ou Comente "QUERO" para receber os detalhes no Direct!`);
                      }}
                      style={{
                        padding: '0.65rem 0.75rem',
                        borderRadius: '10px',
                        border: isSelected ? '2px solid #ec4899' : isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
                        background: isSelected ? (isLight ? '#fdf2f8' : 'rgba(236, 72, 153, 0.15)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)'),
                        color: isSelected ? (isLight ? '#db2777' : '#f472b6') : (isLight ? '#334155' : '#cbd5e1'),
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '0.2rem',
                        textAlign: 'left',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: '100%', justifyContent: 'space-between' }}>
                        <strong style={{ fontSize: '0.8rem' }}>Step {stepNum}</strong>
                        {isSelected && <Check size={12} />}
                      </div>
                      <span style={{ fontSize: '0.7rem', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {c.angleName || c.consciousnessLevel || `Criativo ${stepNum}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. Seleção de Imagem do Step Selecionado */}
            {(() => {
              const currentCreative = creatives.find(c => (c.stepOrder || 1) === storySelectedStep) || creatives[0];
              if (!currentCreative) return null;

              // Monta lista de todas as opções de imagem do criativo (asset principal + histórico)
              const availableImages: Array<{ url: string; label: string }> = [];
              if (currentCreative.assetUrl) {
                availableImages.push({ url: currentCreative.assetUrl, label: 'Arte Atual' });
              }
              if (currentCreative.imageHistory && currentCreative.imageHistory.length > 0) {
                currentCreative.imageHistory.forEach((hist, hIdx) => {
                  if (!availableImages.some(img => img.url === hist.url)) {
                    availableImages.push({ url: hist.url, label: `Versão ${hIdx + 1}` });
                  }
                });
              }

              const activeSelectedUrl = storySelectedImageUrl || currentCreative.assetUrl || '';

              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', alignItems: 'start' }}>
                  {/* Lista de Imagens Disponíveis */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      2. Escolha a Imagem deste Step ({availableImages.length} disponíveis):
                    </label>

                    {availableImages.length === 0 ? (
                      <div style={{ padding: '1.5rem', textAlign: 'center', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', borderRadius: '10px', border: isLight ? '1px dashed #cbd5e1' : '1px dashed rgba(255,255,255,0.1)', color: isLight ? '#64748b' : 'var(--text-muted)', fontSize: '0.8rem' }}>
                        Nenhuma imagem gerada ainda para o Step {storySelectedStep}.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '0.75rem', maxHeight: '240px', overflowY: 'auto', padding: '0.25rem' }}>
                        {availableImages.map((img, iIdx) => {
                          const isChosen = activeSelectedUrl === img.url;
                          return (
                            <div
                              key={img.url + iIdx}
                              onClick={() => setStorySelectedImageUrl(img.url)}
                              style={{
                                position: 'relative',
                                aspectRatio: '9/16',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: isChosen ? '3px solid #ec4899' : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
                                boxShadow: isChosen ? '0 0 14px rgba(236, 72, 153, 0.4)' : 'none',
                                transform: isChosen ? 'scale(1.03)' : 'scale(1)',
                                transition: 'all 0.2s ease',
                                background: '#000'
                              }}
                            >
                              <img src={img.url} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <div style={{ position: 'absolute', bottom: '2px', left: '2px', right: '2px', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.6rem', textAlign: 'center', padding: '0.1rem', borderRadius: '4px' }}>
                                {img.label}
                              </div>
                              {isChosen && (
                                <div style={{ position: 'absolute', top: '3px', right: '3px', background: '#ec4899', color: '#fff', width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Check size={11} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Copy / Legenda */}
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.35rem' }}>
                        Copy / Comentário Automático no Story:
                      </label>
                      <textarea
                        value={storyCustomCaption}
                        onChange={(e) => setStoryCustomCaption(e.target.value)}
                        rows={3}
                        placeholder="Mensagem persuasiva com chamada QUERO..."
                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#f8fafc' : '#27272a', color: isLight ? '#0f172a' : '#fff', fontSize: '0.8rem', resize: 'vertical' }}
                      />
                    </div>
                  </div>

                  {/* Mockup Preview de Story Vertical 9:16 */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Prévia do Story (9:16)
                    </label>
                    <div style={{
                      position: 'relative',
                      width: '180px',
                      height: '320px',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      background: '#000',
                      border: '4px solid #333',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.5)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '0.75rem'
                    }}>
                      {activeSelectedUrl ? (
                        <img 
                          src={activeSelectedUrl} 
                          alt="Story Preview" 
                          className="campaign-story-preview-image"
                        />
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.75rem', textAlign: 'center', padding: '1rem' }}>
                          Selecione uma imagem
                        </div>
                      )}

                      {/* Header Mockup */}
                      <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#ec4899' }} />
                        <span style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>Sua Marca</span>
                      </div>

                      {/* Sticker / Chamada QUERO */}
                      <div style={{ position: 'relative', zIndex: 2, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', padding: '0.4rem 0.6rem', borderRadius: '8px', border: '1px solid rgba(236,72,153,0.5)', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.6rem', color: '#f472b6', fontWeight: 800 }}>💬 Comente QUERO</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Ações do Modal */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setIsStoryModalOpen(false)}
                className="secondary-button"
                style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const target = creatives.find(c => (c.stepOrder || 1) === storySelectedStep) || creatives[0];
                  publishStoriesMutation.mutate({
                    creativeId: target?.id,
                    stepOrder: storySelectedStep,
                    imageUrl: storySelectedImageUrl || target?.assetUrl,
                    caption: storyCustomCaption
                  });
                }}
                disabled={publishStoriesMutation.isPending || !storySelectedImageUrl}
                className="primary-button"
                style={{
                  padding: '0.65rem 1.5rem',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #ec4899, #f59e0b)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  boxShadow: '0 4px 15px rgba(236, 72, 153, 0.4)'
                }}
              >
                {publishStoriesMutation.isPending ? <Loader2 size={16} className="spin" /> : <Zap size={16} />}
                {publishStoriesMutation.isPending ? 'Publicando no Instagram...' : `🚀 Publicar Step ${storySelectedStep} nos Stories`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Prévia e Edição da Legenda do Carrossel (5 Slides) */}
      {isCarouselModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(8px)' }}>
          <div style={{ background: isLight ? '#ffffff' : '#141418', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', borderRadius: '20px', padding: '1.75rem', width: '100%', maxWidth: '840px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #a855f7, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <Layers size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: isLight ? '#0f172a' : '#fff', fontSize: '1.15rem', fontWeight: 700 }}>
                    📸 Publicar Carrossel Completo no Feed
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                    Revise as 5 lâminas da esteira e personalize a legenda contínua antes de enviar ao Instagram
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setIsCarouselModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#64748b' : '#94a3b8', cursor: 'pointer', padding: '0.4rem', borderRadius: '8px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Slides Preview Grid */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                1. As 5 Lâminas do Carrossel:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                {[1, 2, 3, 4, 5].map((stepNum) => {
                  const cr = creatives.find(c => (c.stepOrder || 1) === stepNum);
                  return (
                    <div 
                      key={stepNum}
                      style={{
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
                        background: '#000',
                        position: 'relative',
                        aspectRatio: '1/1',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {cr?.assetUrl ? (
                        <img src={cr.assetUrl} alt={`Slide ${stepNum}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.7rem', textAlign: 'center', padding: '0.25rem' }}>
                          Sem imagem
                        </div>
                      )}
                      <div style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: '4px' }}>
                        #{stepNum}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Editor de Legenda Fluida */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  2. Legenda Narrativa (Fluida e Sem Repetições):
                </label>
                <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                  Edite livremente antes de publicar
                </span>
              </div>
              <textarea
                value={carouselCustomCaption}
                onChange={(e) => setCarouselCustomCaption(e.target.value)}
                rows={8}
                placeholder="A legenda fluida conectando os 5 passos aparecerá aqui..."
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  borderRadius: '10px',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)',
                  background: isLight ? '#f8fafc' : '#27272a',
                  color: isLight ? '#0f172a' : '#fff',
                  fontSize: '0.85rem',
                  lineHeight: '1.5',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Ações do Modal */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setIsCarouselModalOpen(false)}
                className="secondary-button"
                style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => publishCarouselMutation.mutate(carouselCustomCaption)}
                disabled={publishCarouselMutation.isPending || creatives.length === 0}
                className="primary-button"
                style={{
                  padding: '0.65rem 1.5rem',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)'
                }}
              >
                {publishCarouselMutation.isPending ? <Loader2 size={16} className="spin" /> : <Layers size={16} />}
                {publishCarouselMutation.isPending ? 'Publicando Carrossel...' : '🚀 Publicar Carrossel no Instagram'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Posicionamento Manual Interativo da Logo em Canvas */}
      {logoPositioningCreative && logoPositioningCreative.assetUrl && (
        <LogoPositionerModal
          isOpen={!!logoPositioningCreative}
          onClose={() => setLogoPositioningCreative(null)}
          backgroundUrl={logoPositioningCreative.assetUrl}
          logoUrl={briefing?.logoOverlayUrl || useSessionStore.getState().activeTenantLogo || useSessionStore.getState().user?.logo_url || 'https://inteligenciaenegocios.com.br/assets/logo.jpg'}
          creativeTitle={`🏷️ Posicionar Logo: [Step ${logoPositioningCreative.stepOrder || 1}] ${logoPositioningCreative.angleName || logoPositioningCreative.headline}`}
          onSave={async (compositedBase64: string) => {
            await uploadImageMutation.mutateAsync({
              creativeId: logoPositioningCreative.id,
              base64: compositedBase64
            });
            alert('✨ Arte composta com a logo aplicada com sucesso!');
          }}
        />
      )}
    </div>
  );
}

interface BriefingEditorProps {
  campaign: Campaign;
  briefing: CampaignBriefing | undefined;
  onUpgradeMCA: (data: Partial<MCAGeneratePayload>) => void;
  isUpgrading: boolean;
  init?: RequestInit;
  onAdvance?: () => void;
}

function BriefingEditor({ campaign, briefing, onUpgradeMCA, isUpgrading, init, onAdvance }: BriefingEditorProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const queryClient = useQueryClient();

  const [editingAvatarId, setEditingAvatarId] = useState<string | null>(null);
  const [editAvatarName, setEditAvatarName] = useState('');
  const [editAvatarRole, setEditAvatarRole] = useState('');
  const [editAvatarPrompt, setEditAvatarPrompt] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');

  // Auto Briefing & Catálogo de Produtos
  const [productId, setProductId] = useState<string>(briefing?.productId || '');
  const [keywordResearchData, setKeywordResearchData] = useState<KeywordResearchResult | null>(() => {
    if (!briefing?.keywordResearchData) return null;
    return typeof briefing.keywordResearchData === 'string'
      ? JSON.parse(briefing.keywordResearchData)
      : briefing.keywordResearchData;
  });
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>(() => {
    if (!briefing?.selectedKeywords) return [];
    return Array.isArray(briefing.selectedKeywords)
      ? briefing.selectedKeywords
      : (typeof briefing.selectedKeywords === 'string' ? JSON.parse(briefing.selectedKeywords || '[]') : []);
  });
  const [expandedPains, setExpandedPains] = useState<string[]>(() => {
    if (!briefing?.expandedPains) return [];
    return Array.isArray(briefing.expandedPains)
      ? briefing.expandedPains
      : (typeof briefing.expandedPains === 'string' ? JSON.parse(briefing.expandedPains || '[]') : []);
  });
  const [isResearching, setIsResearching] = useState<boolean>(false);

  const [campaignName, setCampaignName] = useState(campaign.name || '');
  const [productOrOffer, setProductOrOffer] = useState(briefing?.productOrOffer || campaign.product_or_offer || campaign.name || '');
  const [valueProposition, setValueProposition] = useState(briefing?.valueProposition || '');
  const [targetAudienceDescription, setTargetAudienceDescription] = useState(briefing?.targetAudienceDescription || campaign.target_audience_description || '');
  const [painPoints, setPainPoints] = useState(briefing?.painPoints || '');
  const [toneOfVoice, setToneOfVoice] = useState(briefing?.toneOfVoice || 'persuasivo_autoridade');
  const [notes, setNotes] = useState(briefing?.notes || '');

  const productsQuery = useQuery({
    queryKey: ['crm-products'],
    queryFn: () => campaignsApi.getProducts(init),
    staleTime: 60000,
  });

  const tenantAvatarsQuery = useQuery({
    queryKey: ['tenant-avatars'],
    queryFn: () => campaignsApi.getTenantAvatars(init),
    staleTime: 120000,
  });

  const generateAvatarsMutation = useMutation({
    mutationFn: (gender?: 'male' | 'female' | void) => campaignsApi.generateTenantAvatars({ gender: gender || undefined, product_or_offer: productOrOffer }, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-avatars'] });
      alert('✨ Personagens do tenant gerados com sucesso!');
    },
    onError: (err: any) => {
      alert('Erro ao gerar personagens: ' + (err.message || 'Falha na requisição'));
    }
  });

  const updateAvatarMutation = useMutation({
    mutationFn: ({ avatarId, data }: { avatarId: string; data: any }) => campaignsApi.updateTenantAvatar(avatarId, data, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-avatars'] });
      setEditingAvatarId(null);
      alert('✅ Dados do personagem salvos com sucesso!');
    },
    onError: (err: any) => {
      alert('Erro ao salvar personagem: ' + (err.message || 'Falha na requisição'));
    }
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: ({ avatarId, file }: { avatarId: string; file: File | string }) => campaignsApi.uploadTenantAvatar(avatarId, file, init),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-avatars'] });
      alert('📸 Foto oficial do personagem atualizada com sucesso no Cloudflare R2!');
    },
    onError: (err: any) => {
      alert('Erro ao enviar foto do personagem: ' + (err.message || 'Falha no upload'));
    }
  });
  
  // Opções acumulativas de Composição Visual na Imagem
  const [includeImage, setIncludeImage] = useState<boolean>(
    briefing?.includeImage !== undefined ? Boolean(briefing.includeImage) : (briefing?.visualOverlayMode === 'image_in_image')
  );
  const tenantDefaultLogo = useSessionStore.getState().activeTenantLogo || useSessionStore.getState().user?.logo_url || '';
  const [logoOverlayUrl, setLogoOverlayUrl] = useState<string>(
    briefing?.logoOverlayUrl || tenantDefaultLogo || 'https://inteligenciaenegocios.com.br/assets/logo.jpg'
  );
  const [includeText, setIncludeText] = useState<boolean>(
    briefing?.includeText !== undefined ? Boolean(briefing.includeText) : (briefing?.visualOverlayMode !== 'pure_image')
  );
  const [customOverlayText, setCustomOverlayText] = useState<string>(
    briefing?.customOverlayText || ''
  );
  const [includeAvatar, setIncludeAvatar] = useState<boolean>(
    briefing?.includeAvatar !== undefined ? Boolean(briefing.includeAvatar) : false
  );
  const [avatarGenderPreference, setAvatarGenderPreference] = useState<'male' | 'female' | 'auto'>(
    briefing?.avatarGenderPreference || 'auto'
  );
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(
    briefing?.selectedAvatarId || null
  );

  // Orçamento, Destino & Segmentação
  const [budgetDaily, setBudgetDaily] = useState<number>(briefing?.budgetDaily || (campaign.budget?.daily ? Number(campaign.budget.daily) : 50));
  const [budgetTotal, setBudgetTotal] = useState<number>(briefing?.budgetTotal || (campaign.budget?.total ? Number(campaign.budget.total) : 1500));
  const [destinationType, setDestinationType] = useState<'whatsapp' | 'lead_form' | 'landing_page' | 'checkout'>(
    (briefing?.destinationType as any) || (campaign.landing_url?.includes('wa.me') ? 'whatsapp' : 'landing_page')
  );
  const [landingUrl, setLandingUrl] = useState<string>(briefing?.landingUrl || campaign.landing_url || '');
  const [whatsappNumber, setWhatsappNumber] = useState<string>('');
  const [geoTargeting, setGeoTargeting] = useState<string>(briefing?.geoTargeting || 'Brasil (Nacional)');
  const [interests, setInterests] = useState<string>(briefing?.interests || '');
  const [metaAdAccountId, setMetaAdAccountId] = useState<string>(briefing?.metaAdAccountId || campaign.meta_ad_account_id || '');
  const [googleCustomerId, setGoogleCustomerId] = useState(briefing?.googleCustomerId || campaign.google_customer_id || '');
  const [adChannels, setAdChannels] = useState<AdChannel[]>(campaignChannels(campaign, briefing));

  const [savedSuccess, setSavedSuccess] = useState(false);
  const [keepExistingImages, setKeepExistingImages] = useState(true);

  useEffect(() => {
    if (briefing) {
      if (briefing.productId) {
        setProductId(briefing.productId);
      } else if (briefing.productOrOffer && productsQuery.data) {
        const prods = (productsQuery.data as any)?.items || productsQuery.data || [];
        const normSearch = briefing.productOrOffer.toLowerCase().trim();
        const found = prods.find((x: any) => x.name && (normSearch.includes(x.name.toLowerCase().trim()) || x.name.toLowerCase().trim().includes(normSearch)));
        if (found) {
          setProductId(found.id);
        }
      }
      if (briefing.selectedKeywords) {
        setSelectedKeywords(
          Array.isArray(briefing.selectedKeywords)
            ? briefing.selectedKeywords
            : (typeof briefing.selectedKeywords === 'string' ? JSON.parse(briefing.selectedKeywords || '[]') : [])
        );
      }
      if (briefing.expandedPains) {
        setExpandedPains(
          Array.isArray(briefing.expandedPains)
            ? briefing.expandedPains
            : (typeof briefing.expandedPains === 'string' ? JSON.parse(briefing.expandedPains || '[]') : [])
        );
      }
      if (briefing.keywordResearchData) {
        setKeywordResearchData(
          typeof briefing.keywordResearchData === 'string'
            ? JSON.parse(briefing.keywordResearchData)
            : briefing.keywordResearchData
        );
      }
      if (campaign.name) setCampaignName(campaign.name);
      if (briefing.productOrOffer) setProductOrOffer(briefing.productOrOffer);
      if (briefing.valueProposition !== undefined) setValueProposition(briefing.valueProposition || '');
      if (briefing.targetAudienceDescription !== undefined) setTargetAudienceDescription(briefing.targetAudienceDescription || '');
      if (briefing.painPoints !== undefined) setPainPoints(briefing.painPoints || '');
      if (briefing.toneOfVoice) setToneOfVoice(briefing.toneOfVoice);
      if (briefing.notes !== undefined) setNotes(briefing.notes || '');
      if (briefing.includeImage !== undefined) setIncludeImage(Boolean(briefing.includeImage));
      else if (briefing.visualOverlayMode) setIncludeImage(briefing.visualOverlayMode === 'image_in_image');
      if (briefing.includeText !== undefined) setIncludeText(Boolean(briefing.includeText));
      else if (briefing.visualOverlayMode) setIncludeText(briefing.visualOverlayMode !== 'pure_image');
      if (briefing.includeAvatar !== undefined) setIncludeAvatar(Boolean(briefing.includeAvatar));
      if (briefing.avatarGenderPreference) setAvatarGenderPreference(briefing.avatarGenderPreference);
      if (briefing.selectedAvatarId !== undefined) setSelectedAvatarId(briefing.selectedAvatarId);
      if (briefing.customOverlayText !== undefined) setCustomOverlayText(briefing.customOverlayText || '');
      if (briefing.logoOverlayUrl) setLogoOverlayUrl(briefing.logoOverlayUrl);
      if (briefing.budgetDaily !== undefined) setBudgetDaily(briefing.budgetDaily);
      if (briefing.budgetTotal !== undefined) setBudgetTotal(briefing.budgetTotal);
      if (briefing.destinationType) setDestinationType(briefing.destinationType as any);
      if (briefing.landingUrl !== undefined) setLandingUrl(briefing.landingUrl);
      if (briefing.geoTargeting) setGeoTargeting(briefing.geoTargeting);
      if (briefing.interests !== undefined) setInterests(briefing.interests || '');
      setAdChannels(campaignChannels(campaign, briefing));
      setGoogleCustomerId(briefing.googleCustomerId || campaign.google_customer_id || '');
      if (briefing.metaAdAccountId !== undefined && briefing.metaAdAccountId !== null) {
        setMetaAdAccountId(briefing.metaAdAccountId);
      }
    }
  }, [briefing, campaign.name]);

  const handleSelectProduct = async (pId: string) => {
    setProductId(pId);
    if (!pId) return;

    const prods = (productsQuery.data as any)?.items || productsQuery.data || [];
    const p = prods.find((x: any) => x.id === pId);
    if (p) {
      if (p.name) setProductOrOffer(p.name);
      if (p.landing_url && !landingUrl) setLandingUrl(p.landing_url);
      if (p.target_segment) setTargetAudienceDescription(p.target_segment);
    }

    try {
      const intel = await campaignsApi.getProductIntelligence(pId, init);
      if (intel) {
        if (intel.pains?.length > 0) {
          setPainPoints(intel.pains.join('; '));
          setExpandedPains(intel.pains);
        }
        if (intel.value_propositions?.length > 0 && !valueProposition) {
          setValueProposition(intel.value_propositions[0]);
        }
      }
    } catch (e) {
      console.warn('Falha ao carregar inteligência do produto:', e);
    }
  };

  const handleRunResearch = async () => {
    setIsResearching(true);
    try {
      const res = await campaignsApi.researchKeywords({
        product_id: productId || undefined,
        budget_initial: budgetDaily,
        custom_product_name: productOrOffer,
        custom_niche: targetAudienceDescription
      }, init);

      if (res.success && res.data) {
        setKeywordResearchData(res.data);
        const allPains = [
          ...(res.data.catalogPains || []),
          ...(res.data.expandedPains || [])
        ];
        setExpandedPains(allPains);
        setSelectedKeywords((res.data.topKeywords || []).map(k => k.keyword));
        if (allPains.length > 0) {
          setPainPoints(allPains.join('; '));
        }
        if (res.data.valueProposition) {
          setValueProposition(res.data.valueProposition);
        }
        if (res.data.notes) {
          setNotes(res.data.notes);
        }
        if (res.data.interests) {
          setInterests(res.data.interests);
        }
      } else {
        alert('Não foi possível obter dados de pesquisa.');
      }
    } catch (err: any) {
      console.error('Erro na pesquisa de keywords:', err);
      alert(`Erro na pesquisa Google/Trends: ${err.message || 'Falha de rede'}`);
    } finally {
      setIsResearching(false);
    }
  };

  const updateBriefingMutation = useMutation({
    mutationFn: ({ status, generateAngles }: { status: 'draft' | 'approved'; generateAngles?: boolean }) => {
      const destinationError = channelDestinationError(adChannels, destinationType, landingUrl);
      if (destinationError) throw new Error(destinationError);
      if (adChannels.includes('meta') && !metaAdAccountId) throw new Error('Selecione a conta Meta Ads.');
      if (adChannels.includes('google') && !googleCustomerId) throw new Error('Selecione a conta Google Ads.');
      // Formatar URL de acordo com o tipo selecionado
      let effectiveUrl = landingUrl;
      if (destinationType === 'whatsapp' && whatsappNumber) {
        const cleanNumber = whatsappNumber.replace(/\D/g, '');
        const message = encodeURIComponent(`Olá! Tenho interesse em saber mais sobre ${productOrOffer || 'a oferta'}.`);
        effectiveUrl = `https://wa.me/${cleanNumber}?text=${message}`;
      } else if (destinationType === 'lead_form') {
        effectiveUrl = 'meta_instant_form';
      }

      return campaignsApi.updateBriefing(
        campaign.id,
        {
          name: campaignName,
          campaignName,
          productId: productId || null,
          productOrOffer,
          valueProposition,
          targetAudienceDescription,
          painPoints,
          selectedKeywords,
          keywordResearchData,
          expandedPains,
          toneOfVoice,
          notes,
          includeImage,
          includeText,
          includeAvatar,
          avatarGenderPreference,
          selectedAvatarId,
          customOverlayText,
          logoOverlayUrl,
          budgetDaily,
          budgetTotal,
          destinationType,
          landingUrl: effectiveUrl,
          geoTargeting,
          interests,
          metaAdAccountId: adChannels.includes('meta') ? metaAdAccountId || null : null,
          googleCustomerId: adChannels.includes('google') ? googleCustomerId || null : null,
          adChannels,
          status,
        },
        init
      );
    },
    onSuccess: (_, { status, generateAngles }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id, 'briefing'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['ai-balance'] });
      queryClient.invalidateQueries({ queryKey: ['ai-packages'] });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);

      if (generateAngles) {
        onUpgradeMCA({
          product_id: productId || undefined,
          product_or_offer: productOrOffer,
          target_audience_description: targetAudienceDescription,
          pain_points: painPoints,
          approved_keywords: selectedKeywords,
          approved_pains: expandedPains,
          keyword_research_data: keywordResearchData || undefined,
          tone: toneOfVoice,
          budget_initial: budgetDaily,
          landing_url: landingUrl,
          include_image: includeImage,
          include_text: includeText,
          include_avatar: includeAvatar,
          avatar_gender_preference: avatarGenderPreference,
          selected_avatar_id: selectedAvatarId || undefined,
          custom_overlay_text: customOverlayText,
          logo_overlay_url: logoOverlayUrl,
          regenerate_images: !keepExistingImages
        });
        if (onAdvance) onAdvance();
      }
    },
    onError: (err: any) => {
      alert('Erro ao salvar briefing: ' + (err.message || 'Falha na requisição'));
    },
  });

  const isApproved = briefing?.status === 'approved';
  const tenantAvatars = tenantAvatarsQuery.data?.items || [];
  const maleAvatar = tenantAvatars.find(a => a.gender === 'male');
  const femaleAvatar = tenantAvatars.find(a => a.gender === 'female');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <CampaignJourneyGuide channels={adChannels} />
      <CampaignChannelSettings campaign={campaign} channels={adChannels} onChannelsChange={setAdChannels}
        metaAccountId={metaAdAccountId} googleCustomerId={googleCustomerId}
        onMetaAccountChange={setMetaAdAccountId} onGoogleAccountChange={setGoogleCustomerId} init={init} />
      {/* Banner de Status do Briefing */}
      <div
        style={{
          background: isApproved 
            ? (isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.08)') 
            : (isLight ? '#fffbeb' : 'rgba(245, 158, 11, 0.08)'),
          border: `1px solid ${isApproved 
            ? (isLight ? '#a7f3d0' : 'rgba(16, 185, 129, 0.25)') 
            : (isLight ? '#fde68a' : 'rgba(245, 158, 11, 0.25)')}`,
          padding: '1rem 1.25rem',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.04)' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: isApproved ? '#10b981' : '#f59e0b',
            }}
          />
          <div>
            <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem', margin: 0, fontWeight: 700 }}>
              {isApproved ? 'Briefing salvo e validado' : 'Briefing em rascunho'}
            </h4>
            <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.8rem', margin: '0.2rem 0 0 0' }}>
              {isApproved
                ? 'Briefing comum aos canais selecionados. Na etapa 2, revise os anúncios específicos de Meta e Google.'
                : 'Defina objetivo, oferta, público, destino e orçamento. Esse briefing orienta os anúncios dos canais selecionados.'}
            </p>
          </div>
        </div>

        <span
          style={{
            fontSize: '0.75rem',
            padding: '0.3rem 0.6rem',
            borderRadius: '6px',
            background: isApproved ? '#10b981' : '#f59e0b',
            color: '#fff',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {isApproved ? 'Aprovado' : 'Rascunho'}
        </span>
      </div>

      {/* SEÇÃO 1: PRODUTO & OFERTA */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)',
          boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
        }}
      >
        <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.6rem' }}>
          📦 1. Produto & Proposta de Valor
        </h4>

        {/* Seleção do Produto do Catálogo (Auto-Preenchimento) */}
        <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '10px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: isLight ? '#0f172a' : '#fff' }}>
              Selecionar Produto Cadastrado no Catálogo
            </label>
            {productsQuery.isLoading && <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>Carregando catálogo...</span>}
          </div>
          <select
            value={productId}
            onChange={(e) => handleSelectProduct(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#121216', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem', fontWeight: 600 }}
          >
            <option value="">-- Selecione para preenchimento automático --</option>
            {((productsQuery.data as any)?.items || productsQuery.data || []).map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.target_segment ? `(${p.target_segment})` : ''}
              </option>
            ))}
          </select>
          <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block', marginTop: '0.35rem' }}>
            Ao escolher um produto, o sistema carrega automaticamente público-alvo, dores mapeadas e proposta de valor do catálogo.
          </span>
        </div>

        <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '1.1rem', borderRadius: '10px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)' }}>
          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.9rem', fontWeight: 700, color: isLight ? '#0f172a' : '#fff' }}>
            Nome Identificador da Campanha *
          </label>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="Ex: Campanha Implantes - Meta Ads e Google Ads"
            style={{ width: '100%', padding: '0.85rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#121216', color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', fontWeight: 600 }}
          />
          <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block', marginTop: '0.35rem' }}>
            Nome exibido na listagem de campanhas e nos relatórios de performance.
          </span>
        </div>

        <div style={{ background: isLight ? '#eef2ff' : 'rgba(99, 102, 241, 0.06)', padding: '1.1rem', borderRadius: '10px', border: isLight ? '1px solid #c7d2fe' : '1px solid rgba(99, 102, 241, 0.25)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: 700, color: isLight ? '#3730a3' : '#a5b4fc' }}>
              Produto / Serviço Anunciado (Oferta Central) *
            </label>
            <button
              type="button"
              onClick={handleRunResearch}
              disabled={isResearching || (!productOrOffer && !productId)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.75rem',
                fontWeight: 700,
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                opacity: isResearching || (!productOrOffer && !productId) ? 0.6 : 1,
                boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)'
              }}
            >
              {isResearching ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
              {isResearching ? 'Preenchendo via IA...' : '✨ Preenchimento Inteligente'}
            </button>
          </div>
          <input
            type="text"
            value={productOrOffer}
            onChange={(e) => setProductOrOffer(e.target.value)}
            placeholder="Ex: Alinhador Invisível / Clareamento Dental / Implantes"
            style={{ width: '100%', padding: '0.85rem', borderRadius: '8px', border: isLight ? '1px solid #818cf8' : '1px solid rgba(99, 102, 241, 0.4)', background: isLight ? '#ffffff' : '#121216', color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', fontWeight: 600 }}
          />
          <span style={{ fontSize: '0.75rem', color: isLight ? '#4338ca' : 'var(--text-muted)', display: 'block', marginTop: '0.35rem' }}>
            Este nome orienta a geração dos anúncios dos canais selecionados.
          </span>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
            Proposta Única de Valor (UVP / Promessa Forte)
          </label>
          <input
            type="text"
            value={valueProposition}
            onChange={(e) => setValueProposition(e.target.value)}
            placeholder="Ex: Sorriso perfeito em até 6 meses com atendimento VIP e sem dor"
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Tom de Voz dos Anúncios
            </label>
            <select
              value={toneOfVoice}
              onChange={(e) => setToneOfVoice(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
            >
              <option value="persuasivo_autoridade">Autoridade & Alta Conversão (Direto ao ponto)</option>
              <option value="urgente_escassez">Urgência & Escassez (Vagas/condições limitadas)</option>
              <option value="educativo_consultivo">Educativo & Consultivo (Quebra de mitos)</option>
              <option value="empatico_emocional">Empático & Emocional (Conexão e acolhimento)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Observações & Requisitos
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Enfatizar consulta de avaliação"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
            />
          </div>
        </div>

        {/* 🎨 SELEÇÃO DO TIPO DE ELEMENTO VISUAL NA ARTE DO CRIATIVO */}
        {adChannels.includes('meta') && <div style={{ marginTop: '0.25rem', paddingTop: '1.25rem', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)' }}>
          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.95rem', fontWeight: 700, color: isLight ? '#0f172a' : '#fff' }}>
            🎨 Composição & Elementos na Imagem Gerada pelo Criativo *
          </label>
          <p style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)', margin: '0 0 1rem 0' }}>
            Marque as opções acumulativas abaixo para compor o prompt, avatares e as camadas visuais da arte gerada pela IA.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            {/* CHECKBOX 1: INCLUIR IMAGEM */}
            <div style={{
              background: includeImage 
                ? (isLight ? '#faf5ff' : 'rgba(168, 85, 247, 0.08)') 
                : (isLight ? '#f8fafc' : '#141418'),
              border: `2px solid ${includeImage ? '#a855f7' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              borderRadius: '12px',
              padding: '1.1rem',
              transition: 'all 0.2s ease',
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={includeImage}
                  onChange={(e) => setIncludeImage(e.target.checked)}
                  style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#a855f7', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>🖼️</span>
                    <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>
                      Incluir Imagem (Logo / Selo)
                    </strong>
                    {includeImage && (
                      <span style={{ fontSize: '0.7rem', background: isLight ? '#f3e8ff' : 'rgba(168,85,247,0.25)', color: isLight ? '#7e22ce' : '#d8b4fe', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 700 }}>
                        ATIVO
                      </span>
                    )}
                  </div>
                  <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.78rem', margin: '0.25rem 0 0 0', lineHeight: 1.4 }}>
                    Insere e compõe a logo oficial sobre a cena com transparência e proporções ajustadas via IA/Canvas.
                  </p>
                </div>
              </label>

              {/* Sub-painel de edição da Imagem/Logo (Abre quando marcado) */}
              {includeImage && (
                <div style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.9rem',
                  borderTop: isLight ? '1px solid #f0abfc' : '1px solid rgba(168, 85, 247, 0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* Preview da Imagem */}
                    <div style={{ 
                      width: '64px', 
                      height: '64px', 
                      borderRadius: '10px', 
                      overflow: 'hidden', 
                      border: isLight ? '2px solid #d946ef' : '2px solid #a855f7', 
                      background: '#fff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      flexShrink: 0, 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                    }}>
                      <img 
                        src={logoOverlayUrl || tenantDefaultLogo || 'https://inteligenciaenegocios.com.br/assets/logo.jpg'} 
                        alt="Logo / Imagem do Tenant" 
                        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} 
                      />
                    </div>

                    {/* Input de URL e Ações */}
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: isLight ? '#701a75' : '#e9d5ff', display: 'block', marginBottom: '0.3rem' }}>
                        URL da Imagem / Logo (ou cole o link):
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="url"
                          value={logoOverlayUrl}
                          onChange={(e) => setLogoOverlayUrl(e.target.value)}
                          placeholder="https://inteligenciaenegocios.com.br/assets/logo.jpg"
                          style={{ 
                            flex: 1, 
                            padding: '0.65rem 0.75rem', 
                            borderRadius: '6px', 
                            border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                            background: isLight ? '#ffffff' : '#141418', 
                            color: isLight ? '#0f172a' : '#fff', 
                            fontSize: '0.85rem' 
                          }}
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const text = await navigator.clipboard.readText();
                              if (text && (text.startsWith('http://') || text.startsWith('https://') || text.startsWith('data:image'))) {
                                setLogoOverlayUrl(text);
                              } else {
                                alert('O conteúdo da área de transferência não é uma URL válida de imagem.');
                              }
                            } catch (_) {
                              const url = prompt('Cole a URL da imagem/logo:');
                              if (url) setLogoOverlayUrl(url);
                            }
                          }}
                          className="secondary-button"
                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                          title="Colar URL da área de transferência"
                        >
                          📋 Colar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CHECKBOX 2: INCLUIR TEXTO */}
            <div style={{
              background: includeText 
                ? (isLight ? '#eef2ff' : 'rgba(99, 102, 241, 0.08)') 
                : (isLight ? '#f8fafc' : '#141418'),
              border: `2px solid ${includeText ? '#6366f1' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              borderRadius: '12px',
              padding: '1.1rem',
              transition: 'all 0.2s ease',
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={includeText}
                  onChange={(e) => setIncludeText(e.target.checked)}
                  style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#6366f1', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>🔤</span>
                    <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>
                      Incluir Texto na Arte
                    </strong>
                    {includeText && (
                      <span style={{ fontSize: '0.7rem', background: isLight ? '#e0e7ff' : 'rgba(99,102,241,0.25)', color: isLight ? '#4338ca' : '#a5b4fc', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 700 }}>
                        ATIVO
                      </span>
                    )}
                  </div>
                  <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.78rem', margin: '0.25rem 0 0 0', lineHeight: 1.4 }}>
                    Gera tipografia frontal de alto impacto no prompt e renderiza texto overlay na imagem.
                  </p>
                </div>
              </label>

              {/* Sub-painel de edição do Texto (Abre quando marcado) */}
              {includeText && (
                <div style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.9rem',
                  borderTop: isLight ? '1px solid #c7d2fe' : '1px solid rgba(99, 102, 241, 0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: isLight ? '#3730a3' : '#c7d2fe' }}>
                    Texto a Inserir na Arte (Opcional):
                  </label>
                  <input
                    type="text"
                    value={customOverlayText}
                    onChange={(e) => setCustomOverlayText(e.target.value)}
                    placeholder="Ex: Alinhadores Invisíveis com 30% OFF (ou deixe vazio para usar o Headline de cada anúncio)"
                    style={{ 
                      width: '100%', 
                      padding: '0.65rem 0.75rem', 
                      borderRadius: '6px', 
                      border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', 
                      background: isLight ? '#ffffff' : '#141418', 
                      color: isLight ? '#0f172a' : '#fff', 
                      fontSize: '0.85rem' 
                    }}
                  />
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.4rem', 
                    fontSize: '0.75rem', 
                    color: isLight ? '#4338ca' : '#a5b4fc', 
                    background: isLight ? '#f5f3ff' : 'rgba(99,102,241,0.12)', 
                    padding: '0.4rem 0.6rem', 
                    borderRadius: '6px' 
                  }}>
                    <span>💡</span>
                    <span>
                      {customOverlayText?.trim() 
                        ? `Texto fixo ativo: "${customOverlayText.trim()}" será aplicado em todos os anúncios.`
                        : 'Sem texto fixo digitado: o sistema incluirá automaticamente o Headline de cada um dos 5 criativos na arte.'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* CHECKBOX 3: USAR AVATAR DA MARCA (PERSONAGENS OFICIAIS DO TENANT) */}
            <div style={{
              background: includeAvatar 
                ? (isLight ? '#f0fdf4' : 'rgba(16, 185, 129, 0.08)') 
                : (isLight ? '#f8fafc' : '#141418'),
              border: `2px solid ${includeAvatar ? '#10b981' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              borderRadius: '12px',
              padding: '1.1rem',
              transition: 'all 0.2s ease',
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={includeAvatar}
                  onChange={(e) => setIncludeAvatar(e.target.checked)}
                  style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#10b981', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>👤</span>
                    <strong style={{ fontSize: '0.95rem', color: isLight ? '#0f172a' : '#fff' }}>
                      Usar Avatar da Marca (Personagens Oficiais)
                    </strong>
                    {includeAvatar && (
                      <span style={{ fontSize: '0.7rem', background: isLight ? '#dcfce7' : 'rgba(16,185,129,0.25)', color: isLight ? '#15803d' : '#86efac', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 700 }}>
                        ATIVO
                      </span>
                    )}
                  </div>
                  <p style={{ color: isLight ? '#475569' : 'var(--text-muted)', fontSize: '0.78rem', margin: '0.25rem 0 0 0', lineHeight: 1.4 }}>
                    Insere pessoas consistentes (1 Avatar Masculino e 1 Avatar Feminino criados para a marca) em todas as peças para construir conexão humana e reconhecimento visual de longo prazo.
                  </p>
                </div>
              </label>

              {/* Sub-painel de visualização e escolha de Avatares (Abre quando marcado) */}
              {includeAvatar && (
                <div style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.9rem',
                  borderTop: isLight ? '1px solid #bbf7d0' : '1px solid rgba(16, 185, 129, 0.25)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.85rem'
                }}>
                  {/* Seletor de Preferência */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, color: isLight ? '#166534' : '#bbf7d0' }}>
                      Seleção do Avatar na Peça:
                    </label>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      {[
                        { key: 'auto', label: '🔄 Automático (Alternar)' },
                        { key: 'male', label: `👨 ${maleAvatar?.name || 'Masculino'}` },
                        { key: 'female', label: `👩 ${femaleAvatar?.name || 'Feminino'}` },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => setAvatarGenderPreference(item.key as any)}
                          style={{
                            padding: '0.35rem 0.65rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: avatarGenderPreference === item.key ? 700 : 500,
                            background: avatarGenderPreference === item.key
                              ? (isLight ? '#10b981' : '#059669')
                              : (isLight ? '#ffffff' : '#1e1e24'),
                            color: avatarGenderPreference === item.key ? '#fff' : (isLight ? '#334155' : '#94a3b8'),
                            border: `1px solid ${avatarGenderPreference === item.key ? '#059669' : (isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)')}`,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cards dos 2 Avatares Oficiais do Tenant */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {[
                      { avt: maleAvatar, gender: 'male', icon: '👨', color: '#3b82f6', defaultName: 'Avatar Masculino', defaultRole: 'Porta-voz da Marca' },
                      { avt: femaleAvatar, gender: 'female', icon: '👩', color: '#ec4899', defaultName: 'Avatar Feminino', defaultRole: 'Embaixadora da Marca' }
                    ].map(({ avt, gender, icon, color, defaultName, defaultRole }) => {
                      const isEditing = editingAvatarId === (avt?.id || gender);
                      return (
                        <div
                          key={gender}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.6rem',
                            padding: '0.85rem',
                            borderRadius: '10px',
                            background: isLight ? '#ffffff' : '#18181b',
                            border: `1px solid ${avatarGenderPreference === gender || avatarGenderPreference === 'auto' ? color : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
                            boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              width: '52px',
                              height: '52px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: `2px solid ${color}`,
                              flexShrink: 0,
                              background: '#1e293b',
                              position: 'relative'
                            }}>
                              <img
                                src={avt?.asset_url || (gender === 'male' ? 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150' : 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150')}
                                alt={avt?.name || defaultName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <span style={{ fontSize: '0.8rem' }}>{icon}</span>
                                <strong style={{ fontSize: '0.85rem', color: isLight ? '#0f172a' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {avt?.name || defaultName}
                                </strong>
                              </div>
                              <span style={{ fontSize: '0.7rem', color: isLight ? color : '#cbd5e1', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {avt?.role_title || defaultRole}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (isEditing) {
                                  setEditingAvatarId(null);
                                } else {
                                  setEditingAvatarId(avt?.id || gender);
                                  setEditAvatarName(avt?.name || '');
                                  setEditAvatarRole(avt?.role_title || '');
                                  setEditAvatarPrompt(avt?.appearance_prompt || '');
                                  setEditAvatarUrl(avt?.asset_url || '');
                                }
                              }}
                              className="secondary-button"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                              title="Editar dados e trocar foto do avatar"
                            >
                              {isEditing ? 'Fechar' : '✏️ Editar'}
                            </button>
                          </div>

                          {/* Formulário Inline de Edição pelo Admin */}
                          {isEditing && (
                            <div style={{
                              marginTop: '0.4rem',
                              paddingTop: '0.6rem',
                              borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: isLight ? '#475569' : '#94a3b8' }}>Nome do Personagem:</label>
                                <input
                                  type="text"
                                  value={editAvatarName}
                                  onChange={(e) => setEditAvatarName(e.target.value)}
                                  placeholder="Ex: Dr. Marcelo / Consultora Helena"
                                  style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: '4px', fontSize: '0.78rem', border: isLight ? '1px solid #cbd5e1' : '1px solid #334155', background: isLight ? '#fff' : '#121216', color: isLight ? '#0f172a' : '#fff' }}
                                />
                              </div>

                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: isLight ? '#475569' : '#94a3b8' }}>Cargo / Papel:</label>
                                <input
                                  type="text"
                                  value={editAvatarRole}
                                  onChange={(e) => setEditAvatarRole(e.target.value)}
                                  placeholder="Ex: Especialista Clínico / Consultora Sênior"
                                  style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: '4px', fontSize: '0.78rem', border: isLight ? '1px solid #cbd5e1' : '1px solid #334155', background: isLight ? '#fff' : '#121216', color: isLight ? '#0f172a' : '#fff' }}
                                />
                              </div>

                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: isLight ? '#475569' : '#94a3b8' }}>Descrição Física / Prompt Visual:</label>
                                <textarea
                                  rows={2}
                                  value={editAvatarPrompt}
                                  onChange={(e) => setEditAvatarPrompt(e.target.value)}
                                  placeholder="Ex: Homem de 35 anos, jaleco médico azul marinho, expressão simpática e acolhedora"
                                  style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', border: isLight ? '1px solid #cbd5e1' : '1px solid #334155', background: isLight ? '#fff' : '#121216', color: isLight ? '#0f172a' : '#fff' }}
                                />
                              </div>

                              {/* Upload de Foto pelo Admin ou URL */}
                              <div>
                                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: isLight ? '#475569' : '#94a3b8' }}>Trocar Foto do Personagem:</label>
                                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.2rem' }}>
                                  <label style={{
                                    flex: 1,
                                    padding: '0.35rem 0.5rem',
                                    borderRadius: '4px',
                                    background: isLight ? '#f1f5f9' : '#27272a',
                                    border: isLight ? '1px dashed #94a3b8' : '1px dashed #52525b',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontSize: '0.72rem',
                                    color: isLight ? '#334155' : '#cbd5e1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.3rem'
                                  }}>
                                    {uploadAvatarMutation.isPending && (uploadAvatarMutation.variables?.avatarId === avt?.id || uploadAvatarMutation.variables?.avatarId === gender) ? (
                                      <>
                                        <Loader2 size={12} className="spin" />
                                        <span>Enviando...</span>
                                      </>
                                    ) : (
                                      <>
                                        <span>📁 Enviar Foto do PC</span>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          style={{ display: 'none' }}
                                          disabled={uploadAvatarMutation.isPending}
                                          onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) {
                                              const targetId = avt?.id || gender;
                                              const reader = new FileReader();
                                              reader.onloadend = () => {
                                                const base64 = reader.result as string;
                                                uploadAvatarMutation.mutate({ avatarId: targetId, file: base64 });
                                              };
                                              reader.readAsDataURL(f);
                                            }
                                          }}
                                        />
                                      </>
                                    )}
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      generateAvatarsMutation.mutate(gender as any);
                                    }}
                                    disabled={generateAvatarsMutation.isPending}
                                    className="secondary-button"
                                    style={{ padding: '0.35rem 0.5rem', fontSize: '0.72rem' }}
                                    title="Regenerar foto via IA"
                                  >
                                    {generateAvatarsMutation.isPending && generateAvatarsMutation.variables === gender ? <Loader2 size={12} className="spin" /> : '✨ Gerar IA'}
                                  </button>
                                </div>
                              </div>

                              {/* Botão Salvar Alterações */}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.3rem' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (avt?.id) {
                                      updateAvatarMutation.mutate({
                                        avatarId: avt.id,
                                        data: {
                                          name: editAvatarName,
                                          role_title: editAvatarRole,
                                          appearance_prompt: editAvatarPrompt
                                        }
                                      });
                                    }
                                  }}
                                  disabled={updateAvatarMutation.isPending}
                                  className="primary-button"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', fontWeight: 600 }}
                                >
                                  {updateAvatarMutation.isPending ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Salvar Alterações
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Ação para regenerar os 2 avatares */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Deseja recriar ambos os personagens da marca adaptados à oferta atual?')) {
                          generateAvatarsMutation.mutate();
                        }
                      }}
                      disabled={generateAvatarsMutation.isPending}
                      className="secondary-button"
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                      {generateAvatarsMutation.isPending ? <Loader2 size={12} className="spin" /> : '✨'} Recriar Ambos com IA
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Aviso se nenhum checkbox estiver marcado (Modo Pura Fotografia) */}
            {!includeImage && !includeText && !includeAvatar && (
              <div style={{
                background: isLight ? '#ecfdf5' : 'rgba(16, 185, 129, 0.08)',
                border: `1px solid ${isLight ? '#a7f3d0' : 'rgba(16, 185, 129, 0.25)'}`,
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                color: isLight ? '#065f46' : '#6ee7b7',
                fontWeight: 600
              }}>
                <span>✨</span>
                <span>Modo Fotografia Pura: As imagens serão geradas sem texto, sem logos e sem personagens fixos.</span>
              </div>
            )}

          </div>
        </div>}
      </div>

      {/* SEÇÃO 2: PÚBLICO-ALVO & SEGMENTAÇÃO */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)',
          boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
        }}
      >
        <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.6rem' }}>
          🎯 2. Público-Alvo & Segmentação
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Localização Geográfica (Cidades / Estados / Raio) *
            </label>
            <input
              type="text"
              value={geoTargeting}
              onChange={(e) => setGeoTargeting(e.target.value)}
              placeholder="Ex: São Paulo - SP (Raio 25km) ou Brasil"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
            />
          </div>

          {adChannels.includes('meta') && <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Interesses & Comportamento (Meta Ads)
            </label>
            <input
              type="text"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              placeholder="Ex: Odontologia Estética, Invisalign, Beleza"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
            />
          </div>}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
            Perfil do Cliente Ideal (ICP & Demografia)
          </label>
          <input
            type="text"
            value={targetAudienceDescription}
            onChange={(e) => setTargetAudienceDescription(e.target.value)}
            placeholder="Ex: Homens e mulheres de 25 a 50 anos que buscam melhorar a autoestima com procedimentos modernos"
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
            Dores Latentes, Objeções & Desejos
          </label>
          <textarea
            rows={2}
            value={painPoints}
            onChange={(e) => setPainPoints(e.target.value)}
            placeholder="Ex: Vergonha de sorrir, medo de dor, achar que o tratamento é inacessível ou demorado"
            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.9rem' }}
          />
        </div>
      </div>

      {/* SEÇÃO 3: ORÇAMENTO & DESTINO DO TRÁFEGO */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: isLight ? '1px solid #e2e8f0' : '1px solid var(--border-color)',
          boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
        }}
      >
        <h4 style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '1rem', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.6rem' }}>
          💰 3. Orçamento & Destino do Tráfego
        </h4>

        {/* Orçamento */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Orçamento Diário (R$/dia) *
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: isLight ? '#64748b' : 'var(--text-muted)', fontSize: '0.9rem' }}>R$</span>
              <input
                type="number"
                min={10}
                step={5}
                value={budgetDaily}
                onChange={(e) => {
                  const daily = Number(e.target.value);
                  setBudgetDaily(daily);
                  setBudgetTotal(daily * 30);
                }}
                style={{ width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.2rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', fontWeight: 600 }}
              />
            </div>
            <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block', marginTop: '0.25rem' }}>
              Recomendado mínimo: R$ 30 a R$ 50/dia
            </span>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              Orçamento Mensal Estimado (R$/mês)
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: isLight ? '#64748b' : 'var(--text-muted)', fontSize: '0.9rem' }}>R$</span>
              <input
                type="number"
                min={100}
                step={50}
                value={budgetTotal}
                onChange={(e) => setBudgetTotal(Number(e.target.value))}
                style={{ width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.2rem', borderRadius: '8px', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', background: isLight ? '#ffffff' : '#141418', color: isLight ? '#0f172a' : '#fff', fontSize: '0.95rem', fontWeight: 600 }}
              />
            </div>
            <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)', display: 'block', marginTop: '0.25rem' }}>
              Projeção para 30 dias de veiculação
            </span>
          </div>
        </div>

        <CampaignTrafficDestination channels={adChannels} destination={destinationType}
          onDestinationChange={setDestinationType} landingUrl={landingUrl} onLandingUrlChange={setLandingUrl}
          whatsappNumber={whatsappNumber} onWhatsappNumberChange={setWhatsappNumber} />
      </div>


      {/* Opção de Preservação de Imagens ao Aprovar e Gerar */}
      {adChannels.includes('meta') && <div
        style={{ 
          background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', 
          padding: '0.85rem 1.1rem', 
          borderRadius: '10px', 
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '0.75rem', 
          cursor: 'pointer' 
        }} 
        onClick={() => setKeepExistingImages(!keepExistingImages)}
      >
        <input 
          type="checkbox" 
          checked={keepExistingImages} 
          onChange={(e) => setKeepExistingImages(e.target.checked)}
          style={{ marginTop: '0.2rem', accentColor: 'var(--primary-color)', cursor: 'pointer' }}
        />
        <div>
          <strong style={{ color: isLight ? '#0f172a' : '#fff', fontSize: '0.85rem', display: 'block' }}>
            🖼️ Preservar imagens visuais já geradas no R2
          </strong>
          <span style={{ fontSize: '0.75rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
            {keepExistingImages 
              ? 'Ao reformular o briefing, a IA atualizará as copies e os ângulos da esteira mantendo as artes visuais que você já aprovou.' 
              : '⚠️ As imagens existentes serão substituídas e novas artes serão sintetizadas pela IA.'}
          </span>
        </div>
      </div>}

      {/* SEÇÃO 5: INTELIGÊNCIA DE PALAVRAS-CHAVE GOOGLE & PROJEÇÃO DE ALCANCE */}
      {keywordResearchData && (
        <div style={{ marginTop: '0.5rem' }}>
          <KeywordResearchPanel
            data={keywordResearchData}
            isLight={isLight}
            mode="interactive"
            selectedKeywords={selectedKeywords}
            selectedPains={expandedPains}
            highlightBudget={budgetDaily}
            onToggleKeyword={(kw) => {
              setSelectedKeywords(prev =>
                prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
              );
            }}
            onTogglePain={(pn) => {
              setExpandedPains(prev =>
                prev.includes(pn) ? prev.filter(p => p !== pn) : [...prev, pn]
              );
            }}
            onAddCustomKeyword={(kw) => {
              if (!selectedKeywords.includes(kw)) {
                setSelectedKeywords(prev => [kw, ...prev]);
              }
            }}
            onAddCustomPain={(pn) => {
              if (!expandedPains.includes(pn)) {
                setExpandedPains(prev => [pn, ...prev]);
              }
            }}
          />
        </div>
      )}

      {/* Aviso de Filosofia do Fluxo: Validação & Edição Prévia */}
      <div style={{
        background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
        border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)',
        borderRadius: '10px',
        padding: '0.85rem 1.15rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginTop: '0.5rem'
      }}>
        <Brain size={18} style={{ color: '#6366f1', flexShrink: 0 }} />
        <div style={{ fontSize: '0.8rem', color: isLight ? '#475569' : 'var(--text-muted)', lineHeight: '1.4' }}>
          <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>Validação & Controle de Conteúdo:</strong> Valide as palavras-chave e a oferta aqui. No <strong>Revisão dos anúncios (Etapa 2)</strong>, confira as artes da Meta e os títulos, descrições e palavras-chave do Google antes de aprovar a publicação.
        </div>
      </div>

      {/* Botões de Ação do Briefing */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {savedSuccess && (
          <span style={{ color: '#059669', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 600, marginRight: 'auto' }}>
            <Check size={14} /> Briefing salvo com sucesso!
          </span>
        )}

        <button
          type="button"
          onClick={() => updateBriefingMutation.mutate({ status: 'draft' })}
          disabled={updateBriefingMutation.isPending || isUpgrading}
          className="secondary-button"
          style={{ padding: '0.7rem 1.15rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', background: isLight ? '#ffffff' : 'transparent', border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)', color: isLight ? '#334155' : 'inherit' }}
        >
          {updateBriefingMutation.isPending && (updateBriefingMutation.variables as any)?.status === 'draft' ? (
            <Loader2 size={15} className="spin" />
          ) : (
            <Save size={15} />
          )}
          Salvar Rascunho
        </button>

        <button
          type="button"
          onClick={() => updateBriefingMutation.mutate({ status: 'approved' })}
          disabled={updateBriefingMutation.isPending || isUpgrading}
          className="secondary-button"
          style={{
            padding: '0.7rem 1.25rem',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            borderColor: '#10b981',
            color: isLight ? '#047857' : '#34d399',
            background: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.08)',
            fontWeight: 600
          }}
          title="Salvar e marcar o briefing como validado sem disparar geração de anúncios"
        >
          {updateBriefingMutation.isPending && (updateBriefingMutation.variables as any)?.status === 'approved' && !(updateBriefingMutation.variables as any)?.generateAngles ? (
            <Loader2 size={15} className="spin" />
          ) : (
            <CheckCircle2 size={15} />
          )}
          Validar e salvar briefing
        </button>

        {(campaign.creatives?.length || 0) === 0 ? (
          <button
            type="button"
            onClick={() => updateBriefingMutation.mutate({ status: 'approved', generateAngles: true })}
            disabled={updateBriefingMutation.isPending || isUpgrading}
            className="primary-button"
            style={{
              padding: '0.7rem 1.4rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1, #a855f7)',
              border: 'none',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
              cursor: 'pointer'
            }}
            title="Salvar o briefing e estruturar as propostas de cópias e ângulos no Estúdio Criativo para você revisar e editar os textos"
          >
            {isUpgrading || (updateBriefingMutation.isPending && (updateBriefingMutation.variables as any)?.generateAngles) ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <Sparkles size={16} />
            )}
            {isUpgrading ? 'Estruturando Textos...' : 'Salvar e gerar propostas dos canais selecionados'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onAdvance}
            className="primary-button"
            style={{
              padding: '0.7rem 1.4rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1, #a855f7)',
              border: 'none',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
              cursor: 'pointer'
            }}
          >
            <span>Avançar para revisão dos anúncios</span>
            <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
